import { useEffect, useState, useMemo, useRef } from 'react';
import type { LeadWithDraft, LeadStatus, LeadScore, SignalType, EmailTone } from '@traction/shared';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import {
  getLeadsWithDrafts,
  updateLeadStatus,
  updateLeadContactInfo,
  updateLeadLocalOverride,
  saveEmailDraft,
  updateEmailDraft,
  getProfile,
  fetchApolloLeads,
  revealLeadEmail,
  toggleLeadStarred,
  learnStyleFromEdit,
  createGmailDraft,
  findSentMessageToRecipientAfter,
  getGmailSendAsSignaturePlain,
  mergeEmailSignaturesForDraft,
  trackSend,
  type CommonGround,
  type LeadLocalOverride,
} from '@traction/api-client';
import { Search, Filter, ExternalLink, Upload, TrendingUp, Zap, Ban, Sparkles, RefreshCw, Edit3, Check, X, Rocket, Mail, Star, Clock, Target, User, Coffee, MapPin, GraduationCap, Building2, Briefcase, Users, Phone, MessageSquare, Copy, ChevronDown, ChevronRight, Plus, Square, CheckSquare, Archive, Download, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { classNames, ROUTES } from '@traction/shared';

const STATUS_TABS: {
  value: LeadStatus | 'all' | 'starred' | 'locals' | 'phones';
  label: string;
  icon?: typeof Star | typeof MapPin | typeof Archive | typeof Phone;
}[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'contacted', label: 'Sent' },
  { value: 'all', label: 'All' },
  { value: 'locals', label: 'Locals', icon: MapPin },
  { value: 'phones', label: 'Phones', icon: Phone },
  { value: 'starred', label: 'Starred', icon: Star },
  { value: 'skipped', label: 'Skipped' },
  { value: 'archived', label: 'Archived', icon: Archive },
];

/** Detail-panel status control (human labels; maps to LeadStatus). */
const PIPELINE_STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'contacted', label: 'Sent' },
  { value: 'replied', label: 'Replied' },
  { value: 'approved', label: 'Approved' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'converted', label: 'Converted' },
  { value: 'archived', label: 'Archived' },
];

const SCORE_OPTIONS: LeadScore[] = ['high', 'medium', 'low'];
const SIGNAL_OPTIONS: SignalType[] = ['funding', 'hiring', 'expansion', 'product_launch', 'leadership_change', 'award', 'partnership'];

const SCORE_CONFIG: Record<LeadScore, { label: string; className: string; color: string }> = {
  high: {
    label: 'High',
    className: 'bg-green-500/20 text-green-400',
    color: 'text-green-400',
  },
  medium: {
    label: 'Medium',
    className: 'bg-cacao-gold/20 text-cacao-gold',
    color: 'text-cacao-gold',
  },
  low: {
    label: 'Low',
    className: 'bg-cacao-dark-tertiary text-cacao-muted',
    color: 'text-cacao-muted',
  },
};

// Infer seniority level from job title
type SeniorityLevel = 'executive' | 'vp' | 'director' | 'manager' | 'individual';
function getSeniorityFromTitle(title: string): { level: SeniorityLevel; label: string; color: string } {
  const t = title.toLowerCase();

  // C-level executives and founders
  if (/\b(ceo|cto|cfo|coo|cmo|cro|cio|cpo|chief|founder|co-founder|cofounder|president|owner)\b/.test(t)) {
    return { level: 'executive', label: 'Executive', color: 'text-purple-400' };
  }

  // Vice Presidents
  if (/\b(vp|vice president|svp|evp|avp)\b/.test(t)) {
    return { level: 'vp', label: 'VP', color: 'text-blue-400' };
  }

  // Directors
  if (/\b(director|head of|principal)\b/.test(t)) {
    return { level: 'director', label: 'Director', color: 'text-cyan-400' };
  }

  // Managers and leads
  if (/\b(manager|lead|supervisor|team lead)\b/.test(t)) {
    return { level: 'manager', label: 'Manager', color: 'text-green-400' };
  }

  // Individual contributor / senior
  return { level: 'individual', label: 'IC', color: 'text-cacao-muted' };
}

interface Filters {
  search: string;
  scores: LeadScore[];
  industries: string[];
  signals: SignalType[];
}

// Outreach type categorization - simplified labels
type OutreachType = 'local' | 'connected' | 'other';

// Metro area mappings - neighborhoods/suburbs that belong to the same metro
const METRO_AREAS: Record<string, string[]> = {
  'new york': ['manhattan', 'brooklyn', 'queens', 'bronx', 'staten island', 'jersey city', 'hoboken', 'newark', 'yonkers', 'white plains', 'stamford', 'long island city', 'astoria', 'williamsburg', 'greenpoint', 'dumbo', 'tribeca', 'soho', 'chelsea', 'harlem'],
  'san francisco': ['sf', 'oakland', 'berkeley', 'palo alto', 'mountain view', 'sunnyvale', 'san jose', 'santa clara', 'fremont', 'hayward', 'daly city', 'san mateo', 'redwood city', 'menlo park', 'cupertino', 'los gatos', 'sausalito', 'mill valley', 'walnut creek', 'pleasanton', 'livermore', 'alameda', 'emeryville', 'south san francisco', 'burlingame'],
  'los angeles': ['la', 'santa monica', 'venice', 'beverly hills', 'hollywood', 'pasadena', 'glendale', 'burbank', 'long beach', 'torrance', 'culver city', 'west hollywood', 'marina del rey', 'malibu', 'irvine', 'anaheim', 'orange county', 'newport beach', 'huntington beach', 'costa mesa', 'fullerton', 'el segundo', 'manhattan beach', 'hermosa beach', 'redondo beach', 'calabasas', 'agoura hills', 'westlake village', 'thousand oaks', 'woodland hills', 'encino', 'sherman oaks', 'studio city', 'tarzana', 'van nuys', 'north hollywood', 'westwood'],
  'chicago': ['evanston', 'oak park', 'naperville', 'schaumburg', 'skokie', 'wicker park', 'lincoln park', 'river north', 'gold coast', 'loop', 'hyde park', 'wrigleyville', 'lakeview', 'bucktown', 'logan square'],
  'boston': ['cambridge', 'somerville', 'brookline', 'newton', 'quincy', 'back bay', 'beacon hill', 'south boston', 'charlestown', 'jamaica plain', 'allston', 'brighton', 'dorchester', 'roxbury'],
  'seattle': ['bellevue', 'redmond', 'kirkland', 'tacoma', 'everett', 'bothell', 'renton', 'kent', 'capitol hill', 'fremont', 'ballard', 'queen anne', 'south lake union', 'pioneer square'],
  'austin': ['round rock', 'cedar park', 'pflugerville', 'georgetown', 'san marcos', 'downtown austin', 'south austin', 'east austin', 'west lake hills', 'lakeway'],
  'denver': ['boulder', 'aurora', 'lakewood', 'littleton', 'arvada', 'westminster', 'thornton', 'highlands', 'lodo', 'rino', 'cherry creek', 'capitol hill'],
  'miami': ['fort lauderdale', 'boca raton', 'west palm beach', 'coral gables', 'miami beach', 'south beach', 'wynwood', 'brickell', 'coconut grove', 'key biscayne', 'aventura', 'doral', 'hialeah'],
  'washington': ['dc', 'washington dc', 'arlington', 'alexandria', 'bethesda', 'silver spring', 'rockville', 'tysons', 'mclean', 'reston', 'fairfax', 'georgetown', 'dupont circle', 'capitol hill', 'foggy bottom', 'adams morgan'],
  'atlanta': ['decatur', 'marietta', 'alpharetta', 'sandy springs', 'buckhead', 'midtown', 'downtown atlanta', 'virginia highland', 'inman park', 'old fourth ward'],
  'dallas': ['fort worth', 'plano', 'frisco', 'irving', 'arlington', 'richardson', 'mckinney', 'addison', 'carrollton', 'uptown', 'deep ellum', 'bishop arts'],
  'phoenix': ['scottsdale', 'tempe', 'mesa', 'chandler', 'gilbert', 'glendale', 'peoria', 'paradise valley'],
  'philadelphia': ['philly', 'camden', 'cherry hill', 'king of prussia', 'conshohocken', 'center city', 'old city', 'rittenhouse', 'university city', 'fishtown', 'northern liberties'],
  'san diego': ['la jolla', 'del mar', 'carlsbad', 'encinitas', 'oceanside', 'chula vista', 'coronado', 'pacific beach', 'downtown san diego', 'gaslamp', 'north park', 'hillcrest'],
  'portland': ['beaverton', 'hillsboro', 'lake oswego', 'tigard', 'gresham', 'pearl district', 'alberta', 'hawthorne', 'sellwood', 'nob hill'],
  'minneapolis': ['st paul', 'saint paul', 'bloomington', 'edina', 'plymouth', 'eden prairie', 'uptown', 'northeast', 'north loop'],
  'detroit': ['ann arbor', 'royal oak', 'birmingham', 'troy', 'farmington hills', 'novi', 'dearborn', 'downtown detroit', 'midtown', 'corktown'],
  'nashville': ['franklin', 'brentwood', 'murfreesboro', 'the gulch', 'east nashville', 'germantown', 'music row', '12 south'],
  'london': ['shoreditch', 'soho', 'mayfair', 'canary wharf', 'westminster', 'chelsea', 'kensington', 'islington', 'hackney', 'camden', 'brixton', 'stratford', 'greenwich', 'richmond', 'wimbledon', 'ealing', 'croydon'],
  'toronto': ['mississauga', 'brampton', 'markham', 'vaughan', 'richmond hill', 'north york', 'scarborough', 'etobicoke', 'downtown toronto', 'liberty village', 'queen west', 'yorkville'],
};

/**
 * Match a normalized city string to a metro alias without substring traps:
 * e.g. token "la" must NOT match inside "atlanta" (old bug: atlanta.includes("la")).
 * Short tokens (≤3 chars) only match as a whole word in the city string.
 */
function cityMatchesMetroToken(cityNorm: string, token: string): boolean {
  const c = cityNorm.toLowerCase().trim();
  const t = token.toLowerCase().trim();
  if (!c || !t) return false;
  if (c === t) return true;

  if (t.length <= 3) {
    const words = c.split(/[\s,_-]+/).filter(Boolean);
    return words.some((w) => w === t);
  }

  return c.includes(t) || t.includes(c);
}

// Get the metro area for a city
function getMetroArea(cityName: string): string | null {
  const city = cityName.toLowerCase().trim();

  // Direct match to metro name
  if (METRO_AREAS[city]) return city;

  // Check if city is in any metro area
  for (const [metro, cities] of Object.entries(METRO_AREAS)) {
    if (cities.includes(city) || cities.some((suburb) => cityMatchesMetroToken(city, suburb))) {
      return metro;
    }
  }

  return null;
}

// Check if two locations are in the same metro area
function isSameMetroArea(loc1: string, loc2: string): { match: boolean; metro?: string } {
  const city1 = loc1.split(',')[0]?.trim().toLowerCase() || '';
  const city2 = loc2.split(',')[0]?.trim().toLowerCase() || '';

  // Direct city match
  if (city1 && city2 && (city1 === city2 || city1.includes(city2) || city2.includes(city1))) {
    return { match: true, metro: city1 };
  }

  // Metro area match
  const metro1 = getMetroArea(city1);
  const metro2 = getMetroArea(city2);

  if (metro1 && metro2 && metro1 === metro2) {
    return { match: true, metro: metro1 };
  }

  // Check if one city is in the other's metro (same safe token rules)
  if (metro1 && METRO_AREAS[metro1]?.some((suburb) => cityMatchesMetroToken(city2, suburb))) {
    return { match: true, metro: metro1 };
  }
  if (metro2 && METRO_AREAS[metro2]?.some((suburb) => cityMatchesMetroToken(city1, suburb))) {
    return { match: true, metro: metro2 };
  }

  return { match: false };
}

/**
 * All strings we compare against lead cities (any hit counts as "local").
 * Order: city first (usually the person's municipality), then free-form location (often a street
 * address — must not block matching when city is "Los Angeles"), then ICP geography targets.
 */
function getUserLocationCandidates(userProfile: unknown): string[] {
  const p = userProfile as {
    city?: string | null;
    location?: string | null;
    target_geography?: string[] | null;
  } | null | undefined;
  if (!p) return [];

  const raw: string[] = [
    (p.city || '').trim(),
    (p.location || '').trim(),
    ...((Array.isArray(p.target_geography) ? p.target_geography : []) as string[])
      .map((g) => (typeof g === 'string' ? g.trim() : ''))
      .filter(Boolean),
  ].filter(Boolean);

  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of raw) {
    const k = s.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      out.push(s);
    }
  }
  return out;
}

/** Prefer lead.location; some records only have hints in enrichment (legacy / partial saves). */
function getEffectiveLeadLocation(lead: LeadWithDraft): string {
  const direct = (lead.location || '').trim();
  if (direct) return direct;
  const ed = lead.enrichment_data as Record<string, unknown> | null | undefined;
  if (!ed || typeof ed !== 'object') return '';
  const city = typeof ed.city === 'string' ? ed.city.trim() : '';
  const state = typeof ed.state === 'string' ? ed.state.trim() : '';
  const country = typeof ed.country === 'string' ? ed.country.trim() : '';
  const joined = [city, state, country].filter(Boolean).join(', ');
  return joined;
}

/** Same city/metro as your profile (ignores manual local override). */
function computeGeographicLocalMatch(userProfile: unknown, lead: LeadWithDraft): boolean {
  const locLead = getEffectiveLeadLocation(lead);
  if (!locLead) return false;
  const candidates = getUserLocationCandidates(userProfile);
  if (candidates.length === 0) return false;
  return candidates.some((c) => isSameMetroArea(c, locLead).match);
}

function getLocalOverride(lead: LeadWithDraft): 'auto' | 'local' | 'not_local' {
  const v = (lead.enrichment_data as Record<string, unknown> | null)?.tractionbee_local_override;
  if (v === 'local' || v === 'not_local') return v;
  return 'auto';
}

/** Locals tab + filters: respects optional manual override stored on the lead. */
function leadMatchesUserLocal(userProfile: unknown, lead: LeadWithDraft): boolean {
  const o = getLocalOverride(lead);
  if (o === 'local') return true;
  if (o === 'not_local') return false;
  return computeGeographicLocalMatch(userProfile, lead);
}

/** Pending lead still needs a real address (missing email or Apollo placeholder). */
function leadNeedsEmailEnrichment(lead: LeadWithDraft): boolean {
  if (lead.status !== 'pending') return false;
  const e = (lead.email || '').trim();
  if (!e) return true;
  return e.includes('@apollo.local');
}

/** True when email is missing or still an Apollo placeholder (for Locals Research tab; includes approved). */
function leadNeedsEmailResearch(lead: LeadWithDraft): boolean {
  if (lead.status !== 'pending' && lead.status !== 'approved') return false;
  const e = (lead.email || '').trim();
  if (!e) return true;
  return e.includes('@apollo.local');
}

function isLocalsNotYetSentStatus(status: LeadStatus): boolean {
  return status === 'pending' || status === 'approved';
}

interface LeadCommonGround {
  type: 'city' | 'metro' | 'school' | 'employer' | 'industry';
  icon: string;
  label: string;
  detail: string;
  strength: 'strong' | 'medium' | 'weak';
}

interface LeadWithOutreach extends LeadWithDraft {
  outreachType: OutreachType;
  outreachReason?: string;
  outreachIcon?: string;
  commonGround?: LeadCommonGround[];
  connectionScore: number; // 0-100 score for sorting
}

function pickOutreachFromCommonGround(
  commonGround: LeadCommonGround[]
): Pick<LeadWithOutreach, 'outreachType' | 'outreachReason' | 'outreachIcon'> {
  const hasLocal = commonGround.some((cg) => cg.type === 'city' || cg.type === 'metro');
  const hasStrongConnection = commonGround.some((cg) => cg.type === 'school' || cg.type === 'employer');

  let outreachType: OutreachType = 'other';
  let outreachReason = 'Targeted outreach';
  let outreachIcon = '📧';

  if (hasLocal && hasStrongConnection) {
    outreachType = 'local';
    outreachReason = 'Local + shared connection';
    outreachIcon = '🔥';
  } else if (hasLocal) {
    outreachType = 'local';
    outreachReason =
      commonGround.find((cg) => cg.type === 'city' || cg.type === 'metro')?.detail || 'Same area';
    outreachIcon = '📍';
  } else if (hasStrongConnection) {
    outreachType = 'connected';
    const strongCG = commonGround.find((cg) => cg.type === 'school' || cg.type === 'employer');
    outreachReason = strongCG?.detail || 'Shared background';
    outreachIcon = strongCG?.icon || '🤝';
  } else if (commonGround.length > 0) {
    outreachType = 'connected';
    outreachReason = commonGround[0].detail;
    outreachIcon = commonGround[0].icon;
  }

  return { outreachType, outreachReason, outreachIcon };
}

// Find ALL shared connections between user and lead - don't return early!
function categorizeLeadOutreach(lead: LeadWithDraft, userProfile: any): LeadWithOutreach {
  const commonGround: LeadCommonGround[] = [];
  let connectionScore = 0;

  // Get enrichment data for deeper matching
  const enrichmentData = (lead as any).enrichment_data || {};
  const leadEducation = enrichmentData.education || [];
  const leadEmployers = enrichmentData.employment_history || [];

  // 1. CHECK FOR LOCAL (COFFEE MEETUP) - Same city or metro area
  const userCandidates = getUserLocationCandidates(userProfile);
  const leadLoc = getEffectiveLeadLocation(lead);
  if (userCandidates.length && leadLoc) {
    let metroMatch: { match: boolean; metro?: string } = { match: false };
    for (const uc of userCandidates) {
      const m = isSameMetroArea(uc, leadLoc);
      if (m.match) {
        metroMatch = m;
        break;
      }
    }

    if (metroMatch.match) {
      const leadCity = leadLoc.split(',')[0]?.trim();
      const leadCityLower = leadCity?.toLowerCase() || '';
      const isExactCity = userCandidates.some(
        (uc) => uc.split(',')[0]?.trim().toLowerCase() === leadCityLower
      );
      const metroName = metroMatch.metro
        ? metroMatch.metro.charAt(0).toUpperCase() + metroMatch.metro.slice(1)
        : leadCity;

      commonGround.push({
        type: isExactCity ? 'city' : 'metro',
        icon: '☕',
        label: isExactCity ? 'Same City' : 'Same Metro',
        detail: isExactCity ? `Both in ${leadCity} - grab coffee!` : `Both in ${metroName} area`,
        strength: isExactCity ? 'strong' : 'medium'
      });
      connectionScore += isExactCity ? 30 : 20;
    }
  }

  // 2. CHECK FOR SHARED EDUCATION - Find ALL matching schools
  const userEducation = userProfile?.education || [];
  const foundSchools = new Set<string>();

  for (const userSchool of userEducation) {
    const userSchoolName = (userSchool.school || userSchool.name || '').toLowerCase();
    if (!userSchoolName || userSchoolName.length < 3) continue;

    for (const leadSchool of leadEducation) {
      const leadSchoolName = (leadSchool.school_name || leadSchool.school || '').toLowerCase();
      if (!leadSchoolName || leadSchoolName.length < 3) continue;

      if ((userSchoolName.includes(leadSchoolName) || leadSchoolName.includes(userSchoolName)) && !foundSchools.has(leadSchoolName)) {
        foundSchools.add(leadSchoolName);
        const schoolDisplay = leadSchool.school_name || leadSchool.school || userSchool.school || userSchool.name;
        commonGround.push({
          type: 'school',
          icon: '🎓',
          label: 'Alumni',
          detail: `Both attended ${schoolDisplay}`,
          strength: 'strong'
        });
        connectionScore += 35;
      }
    }
  }

  // 3. CHECK FOR SHARED PAST EMPLOYERS - Find ALL matching companies
  const userEmployers = userProfile?.employment_history || [];
  const foundCompanies = new Set<string>();

  for (const userJob of userEmployers) {
    const userCompany = (userJob.company || userJob.organization_name || '').toLowerCase();
    if (!userCompany || userCompany.length < 3) continue;

    for (const leadJob of leadEmployers) {
      const leadCompany = (leadJob.organization_name || leadJob.company || '').toLowerCase();
      if (!leadCompany || leadCompany.length < 3) continue;

      if ((userCompany.includes(leadCompany) || leadCompany.includes(userCompany)) && !foundCompanies.has(leadCompany)) {
        foundCompanies.add(leadCompany);
        const companyDisplay = leadJob.organization_name || leadJob.company || userJob.company || userJob.organization_name;
        commonGround.push({
          type: 'employer',
          icon: '💼',
          label: 'Shared Company',
          detail: `Both worked at ${companyDisplay}`,
          strength: 'strong'
        });
        connectionScore += 40;
      }
    }
  }

  // 4. CHECK FOR SAME INDUSTRY
  const userIndustries = userProfile?.company_industry;
  if (userIndustries && lead.company_industry) {
    const leadInd = lead.company_industry.toLowerCase();
    const industriesToCheck = Array.isArray(userIndustries) ? userIndustries : [userIndustries];

    for (const userInd of industriesToCheck) {
      if (typeof userInd === 'string') {
        const userIndLower = userInd.toLowerCase();
        if (userIndLower === leadInd || userIndLower.includes(leadInd) || leadInd.includes(userIndLower)) {
          commonGround.push({
            type: 'industry',
            icon: '🏢',
            label: 'Same Industry',
            detail: lead.company_industry,
            strength: 'medium'
          });
          connectionScore += 15;
          break; // Only add industry once
        }
      }
    }
  }

  let { outreachType, outreachReason, outreachIcon } = pickOutreachFromCommonGround(commonGround);

  const localOverride = enrichmentData.tractionbee_local_override as 'local' | 'not_local' | undefined;
  if (localOverride === 'local') {
    outreachType = 'local';
    outreachReason = 'Marked as local';
    outreachIcon = '📍';
  } else if (localOverride === 'not_local') {
    const sansLocal = commonGround.filter((cg) => cg.type !== 'city' && cg.type !== 'metro');
    ({ outreachType, outreachReason, outreachIcon } = pickOutreachFromCommonGround(sansLocal));
  }

  return {
    ...lead,
    outreachType,
    outreachReason,
    outreachIcon,
    commonGround,
    connectionScore,
  };
}

/** Matches supabase generate-email default: title-only subject, short direct body. */
function formatPhoneBrief(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

function defaultOutreachSubject(profile: any): string {
  return ((profile?.current_title || 'CTO') as string).trim() || 'Quick intro';
}

/** Distinct inbox line when using the Friday coffee template (matches body CTA). */
function defaultOutreachCoffeeSubject(profile: any): string {
  const title = ((profile?.current_title || 'CTO') as string).trim() || 'Quick intro';
  return `${title} - Coffee Friday?`;
}

function defaultOutreachBody(lead: LeadWithDraft, profile: any): string {
  const myTitle = ((profile?.current_title || 'CTO') as string).trim();
  const myCityRaw = (profile?.city || profile?.location || '').trim();
  const myCityShort = myCityRaw ? myCityRaw.split(',')[0].trim() : '';
  const myPhone = profile?.phone ? formatPhoneBrief(profile.phone) : '';
  const cityPhrase = myCityShort ? ` in ${myCityShort}` : '';
  const phonePhrase = myPhone ? ` ${myPhone} (text is fine too).` : '';
  const company = lead.company_name || 'your team';
  const first = lead.first_name || 'there';
  return `Hi ${first}, I'm a ${myTitle}${cityPhrase}. MVPs and senior web engineering. Open to a quick call if that's useful for ${company}?${phonePhrase}`;
}

/** Coffee add-on: direct, casual. Same city vs two cities vs one hint (no geocoding). */
function suggestCoffeeMeetLine(lead: LeadWithDraft, profile: any): string {
  const leadCity = getEffectiveLeadLocation(lead).split(',')[0]?.trim();
  const userRaw = (profile?.city || profile?.location || '').trim();
  const userCity = userRaw ? userRaw.split(',')[0].trim() : '';
  if (leadCity && userCity && leadCity.toLowerCase() === userCity.toLowerCase()) {
    return `I noticed we're both in ${leadCity}, want to catch a coffee Friday around 9am? We could pick a spot between us to keep it easy.`;
  }
  if (leadCity && userCity && leadCity.toLowerCase() !== userCity.toLowerCase()) {
    return `I'm in ${userCity} and you're in ${leadCity}. Want to grab coffee Friday around 9am? We could pick somewhere between us to keep it easy.`;
  }
  if (leadCity || userCity) {
    const hub = leadCity || userCity;
    return `If you're up for it, coffee Friday around 9am works. I can suggest a spot near ${hub} or you pick whatever's easy.`;
  }
  return `Coffee Friday around 9am works if you want to meet in person. Whatever's easiest for you.`;
}

function defaultOutreachCoffeeBody(lead: LeadWithDraft, profile: any): string {
  return `${defaultOutreachBody(lead, profile)} ${suggestCoffeeMeetLine(lead, profile)}`;
}

/**
 * Callable digits from `leads.phone` or Apollo `enrichment_data.phone_numbers` (sourced / legacy rows
 * often only had numbers in JSON).
 */
function getLeadPhoneDigits(lead: Pick<LeadWithDraft, 'phone' | 'enrichment_data'>): string {
  const direct = String(lead.phone || '').replace(/\D/g, '');
  if (direct.length >= 10) return direct;

  const ed = lead.enrichment_data as Record<string, unknown> | null | undefined;
  const nums = ed?.phone_numbers;
  if (!Array.isArray(nums)) return direct;

  for (const entry of nums) {
    if (!entry || typeof entry !== 'object') continue;
    const p = entry as { sanitized_number?: string; raw_number?: string };
    const s = String(p.sanitized_number || p.raw_number || '').replace(/\D/g, '');
    if (s.length >= 10) return s;
  }
  return direct;
}

function leadHasCallablePhone(lead: Pick<LeadWithDraft, 'phone' | 'enrichment_data'>): boolean {
  return getLeadPhoneDigits(lead).length >= 10;
}

/** Display / SMS: prefer `leads.phone` when it has enough digits, else first callable Apollo number. */
function getLeadPhoneForUi(lead: LeadWithDraft): string | null {
  if (getLeadPhoneDigits(lead).length < 10) return null;

  const col = (lead.phone || '').trim();
  if (col && col.replace(/\D/g, '').length >= 10) return col;

  const ed = lead.enrichment_data as Record<string, unknown> | null | undefined;
  const nums = ed?.phone_numbers;
  if (!Array.isArray(nums)) return null;
  for (const entry of nums) {
    if (!entry || typeof entry !== 'object') continue;
    const p = entry as { sanitized_number?: string; raw_number?: string };
    const raw = (p.sanitized_number || p.raw_number || '').trim();
    if (raw && String(raw).replace(/\D/g, '').length >= 10) return raw;
  }
  return null;
}

/** Prefills Apple Messages (macOS / iOS) via sms: scheme. */
function buildSmsDraftUrl(phoneRaw: string, body: string): string | null {
  const digits = phoneRaw.replace(/\D/g, '');
  if (digits.length < 10) return null;
  const e164 =
    digits.length === 10
      ? `+1${digits}`
      : digits.length === 11 && digits.startsWith('1')
        ? `+${digits}`
        : `+${digits}`;
  const trimmed = body.trim();
  if (!trimmed) return `sms:${e164}`;
  return `sms:${e164}?body=${encodeURIComponent(trimmed)}`;
}

function defaultSmsBody(lead: LeadWithDraft, profile: any): string {
  const myTitle = ((profile?.current_title || 'CTO') as string).trim();
  const myCityRaw = (profile?.city || profile?.location || '').trim();
  const myCityShort = myCityRaw ? myCityRaw.split(',')[0].trim() : '';
  const cityBit = myCityShort ? ` in ${myCityShort}` : '';
  const first = lead.first_name || 'there';
  return `Hi ${first}, I'm a ${myTitle}${cityBit}. Open to a quick text or call about MVP or senior web work if useful?`;
}

/** Gmail Sent polling: how long to watch after opening compose / creating a draft. */
const GMAIL_SENT_WATCH_TTL_MS = 2 * 60 * 60 * 1000;
const GMAIL_POLL_INTERVAL_MS = 32_000;
const GMAIL_FIRST_POLL_DELAY_MS = 12_000;

/** Collapse whitespace/newlines so outreach reads as one paragraph before signatures are appended. */
function collapseOutreachParagraph(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

const CSV_EXPORT_HEADERS = [
  'first_name',
  'last_name',
  'email',
  'title',
  'company_name',
  'company_industry',
  'location',
  'phone',
  'linkedin_url',
  'score',
  'status',
  'signal_type',
] as const;

function escapeCsvField(val: string | number | null | undefined): string {
  const s = val == null || val === undefined ? '' : String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadLeadsCsv(rows: LeadWithDraft[], filename: string) {
  const headerLine = CSV_EXPORT_HEADERS.join(',');
  const body = rows
    .map((lead) =>
      CSV_EXPORT_HEADERS.map((h) =>
        escapeCsvField((lead as unknown as Record<string, string | number | null | undefined>)[h])
      ).join(',')
    )
    .join('\n');
  const bom = '\uFEFF';
  const blob = new Blob([bom + headerLine + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function leadCommonGroundToUi(cg: LeadCommonGround[]): CommonGround[] {
  return cg.map((c) => {
    let type: CommonGround['type'] = 'role';
    if (c.type === 'city' || c.type === 'metro') type = 'city';
    else if (c.type === 'school') type = 'college';
    else if (c.type === 'employer') type = 'workplace';
    else if (c.type === 'industry') type = 'industry';
    return { type, match: c.label, details: c.detail };
  });
}

export function LeadsPage() {
  const { user, gmailAccessToken } = useAuth();
  const toast = useToast();
  const [leads, setLeads] = useState<LeadWithDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<LeadStatus | 'all' | 'starred' | 'locals' | 'phones'>('pending');
  /** Sub-segmentation inside Locals: Sent / Pending (ready) / Research (no real email yet). */
  const [localsSubTab, setLocalsSubTab] = useState<'sent' | 'pending' | 'research'>('pending');
  const [selectedLead, setSelectedLead] = useState<LeadWithDraft | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    search: '',
    scores: [],
    industries: [],
    signals: [],
  });

  // Lead sourcing state
  const [isSourcingLeads, setIsSourcingLeads] = useState(false);

  // Email drafting state
  const [isEditingDraft, setIsEditingDraft] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [draftTone, setDraftTone] = useState<EmailTone>('warm');
  const [draftId, setDraftId] = useState<string | null>(null);
  // Original draft for style learning comparison
  const [originalDraft, setOriginalDraft] = useState<{ subject: string; body: string } | null>(null);

  // Common ground state (from AI generation)
  const [commonGround, setCommonGround] = useState<CommonGround[]>([]);
  const [sameCity, setSameCity] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [copiedDraft, setCopiedDraft] = useState(false);

  const [smsDraftBody, setSmsDraftBody] = useState('');
  const [copiedSms, setCopiedSms] = useState(false);

  // Editable contact info state
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [editingEmail, setEditingEmail] = useState('');
  const [editingPhone, setEditingPhone] = useState('');

  // User profile for outreach categorization
  const [userProfile, setUserProfile] = useState<any>(null);

  // Section collapse state
  const [collapsedSections, setCollapsedSections] = useState<Record<OutreachType, boolean>>({
    local: false,
    connected: false,
    other: false,
  });

  // Bulk selection state
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [isBulkEnriching, setIsBulkEnriching] = useState(false);
  const [bulkEnrichProgress, setBulkEnrichProgress] = useState({ current: 0, total: 0 });

  /** While set, poll Gmail Sent for a message to this lead after `notBefore` (requires Google + Gmail scopes). */
  const [gmailSendWatch, setGmailSendWatch] = useState<{
    leadId: string;
    notBefore: number;
    expiresAt: number;
  } | null>(null);

  const activeFilterCount = filters.scores.length + filters.industries.length + filters.signals.length;

  /** Draft exists in UI or DB — do not rely on subject alone (AI can omit subject). */
  const hasDraft =
    isEditingDraft ||
    Boolean(selectedLead?.email_draft) ||
    draftSubject.trim().length > 0 ||
    draftBody.trim().length > 0;

  const canUseGmail =
    Boolean(selectedLead?.email && !selectedLead.email.includes('@apollo.local'));

  const statusesWithGmailCompose: LeadStatus[] = ['pending', 'contacted', 'replied', 'approved'];

  /** Pending + Gmail compose: show Mark as sent beside Open in Gmail so the pipeline update isn’t missed. */
  const showMarkSentNextToGmail =
    canUseGmail && hasDraft && selectedLead?.status === 'pending';

  useEffect(() => {
    let isMounted = true;
    async function loadLeads() {
      if (!user) return;
      try {
        const allLeads = await getLeadsWithDrafts(user.uid);
        if (isMounted) setLeads(allLeads);
      } catch (error) {
        console.error('Failed to load leads:', error);
        if (isMounted) toast.error('Failed to load leads', 'Please refresh the page');
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadLeads();
    return () => { isMounted = false; };
  }, [user]);

  // Load user profile for outreach categorization
  useEffect(() => {
    let isMounted = true;
    async function loadProfile() {
      if (!user) return;
      try {
        const profile = await getProfile(user.uid);
        if (isMounted) setUserProfile(profile);
      } catch (error) {
        console.error('Failed to load profile:', error);
      }
    }
    loadProfile();
    return () => { isMounted = false; };
  }, [user]);

  async function handleSourceLeads() {
    if (!user) return;

    setIsSourcingLeads(true);

    try {
      const result = await fetchApolloLeads(user.uid, 25);

      if (result.success) {
        toast.success(`Found ${result.stats.inserted} new leads!`, 'Your pipeline is growing');
        // Reload leads to show the new ones
        const allLeads = await getLeadsWithDrafts(user.uid);
        setLeads(allLeads);
      } else {
        toast.error('Failed to source leads', result.error || 'Please try again');
      }
    } catch (error: any) {
      toast.error('Failed to source leads', error.message || 'Please try again');
    } finally {
      setIsSourcingLeads(false);
    }
  }

  async function applyLeadPipelineStatus(
    leadId: string,
    nextStatus: LeadStatus,
    options?: {
      closePanel?: boolean;
      successToast?: string;
      successDetail?: string;
      skipTrackSend?: boolean;
    }
  ) {
    const leadForTrack =
      leads.find((l) => l.id === leadId) ?? (selectedLead?.id === leadId ? selectedLead : undefined);
    const prevStatus = leadForTrack?.status;
    const draftIdForTrack = leadForTrack?.email_draft?.id;
    const now = new Date().toISOString();

    try {
      await updateLeadStatus(leadId, nextStatus);

      setLeads((prev) =>
        prev.map((l) => {
          if (l.id !== leadId) return l;
          const patch: Partial<LeadWithDraft> = { status: nextStatus };
          if (nextStatus === 'contacted') patch.contacted_at = now;
          if (nextStatus === 'replied') patch.replied_at = now;
          return { ...l, ...patch };
        })
      );

      if (nextStatus !== 'pending') {
        setGmailSendWatch(null);
      }

      if (options?.closePanel) {
        setSelectedLead(null);
      } else {
        setSelectedLead((prev) => {
          if (!prev || prev.id !== leadId) return prev;
          const patch: Partial<LeadWithDraft> = { status: nextStatus };
          if (nextStatus === 'contacted') patch.contacted_at = now;
          if (nextStatus === 'replied') patch.replied_at = now;
          return { ...prev, ...patch };
        });
      }

      if (
        nextStatus === 'contacted' &&
        user &&
        prevStatus !== 'contacted' &&
        !options?.skipTrackSend
      ) {
        void trackSend(user.uid, leadId, draftIdForTrack, 'webapp').then((res) => {
          if (!res.success) console.warn('trackSend failed:', res.error);
        });
      }

      if (options?.successToast) {
        toast.success(options.successToast, options.successDetail);
      }
    } catch (error) {
      console.error('Failed to update lead status:', error);
      const msg = error instanceof Error ? error.message : 'Please try again';
      toast.error('Could not update status', msg);
    }
  }

  async function handleAction(
    leadId: string,
    action: 'approve' | 'skip' | 'reject',
    options?: { approveToast?: string; approveToastDetail?: string }
  ) {
    const statusMap: Record<string, LeadStatus> = {
      approve: 'contacted',
      skip: 'skipped',
      reject: 'rejected',
    };
    const next = statusMap[action];
    const successToast =
      action === 'approve'
        ? options?.approveToast ?? 'Marked as sent'
        : action === 'skip'
          ? 'Skipped'
          : 'Lead updated';

    await applyLeadPipelineStatus(leadId, next, {
      closePanel: true,
      successToast,
      successDetail: action === 'approve' ? options?.approveToastDetail : undefined,
    });
  }

  const handleActionRef = useRef(handleAction);
  handleActionRef.current = handleAction;

  function startGmailSentWatch(leadId: string) {
    const now = Date.now();
    setGmailSendWatch({
      leadId,
      notBefore: now - 5000,
      expiresAt: now + GMAIL_SENT_WATCH_TTL_MS,
    });
  }

  useEffect(() => {
    if (!selectedLead) {
      setGmailSendWatch(null);
      return;
    }
    setGmailSendWatch((w) => (w && w.leadId !== selectedLead.id ? null : w));
  }, [selectedLead?.id]);

  useEffect(() => {
    if (!gmailAccessToken || !gmailSendWatch) return;
    if (!selectedLead || selectedLead.status !== 'pending') return;
    if (selectedLead.id !== gmailSendWatch.leadId) return;
    if (selectedLead.email.includes('@apollo.local')) return;
    if (Date.now() > gmailSendWatch.expiresAt) {
      setGmailSendWatch(null);
      return;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    const watchSnapshot = gmailSendWatch;

    async function tick() {
      if (cancelled) return;
      if (Date.now() > watchSnapshot.expiresAt) {
        setGmailSendWatch(null);
        return;
      }
      const pollEmail = selectedLead?.email;
      if (!pollEmail || pollEmail.includes('@apollo.local')) return;
      if (!gmailAccessToken) return;
      const result = await findSentMessageToRecipientAfter(
        gmailAccessToken,
        pollEmail,
        watchSnapshot.notBefore
      );
      if (cancelled) return;
      if (selectedLead?.id !== watchSnapshot.leadId || selectedLead.status !== 'pending') return;
      if (result === 'found') {
        cancelled = true;
        if (intervalId) clearInterval(intervalId);
        setGmailSendWatch(null);
        await handleActionRef.current(selectedLead!.id, 'approve', {
          approveToast: 'Sent detected in Gmail',
          approveToastDetail: 'Lead marked as contacted',
        });
        return;
      }
      if (result === 'auth_error') {
        cancelled = true;
        if (intervalId) clearInterval(intervalId);
        setGmailSendWatch(null);
        toast.error(
          'Gmail connection expired',
          'Sign in with Google again to auto-detect sends from Gmail.'
        );
      }
    }

    const firstTimer = setTimeout(() => void tick(), GMAIL_FIRST_POLL_DELAY_MS);
    intervalId = setInterval(() => void tick(), GMAIL_POLL_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearTimeout(firstTimer);
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [
    gmailAccessToken,
    gmailSendWatch,
    selectedLead?.id,
    selectedLead?.email,
    selectedLead?.status,
    toast,
  ]);

  async function handleArchiveLead(leadId: string) {
    await applyLeadPipelineStatus(leadId, 'archived', { successToast: 'Lead archived' });
  }

  async function handleBulkArchive() {
    if (selectedLeads.size === 0) return;

    const leadsToArchive = leads.filter(
      (l) => selectedLeads.has(l.id) && l.status !== 'archived'
    );

    if (leadsToArchive.length === 0) {
      toast.info('No leads to archive', 'Selected leads are already archived');
      return;
    }

    let successCount = 0;
    for (const lead of leadsToArchive) {
      try {
        await updateLeadStatus(lead.id, 'archived');
        setLeads((prev) =>
          prev.map((l) => (l.id === lead.id ? { ...l, status: 'archived' } : l))
        );
        successCount++;
      } catch (error) {
        console.error(`Failed to archive lead ${lead.id}:`, error);
      }
    }

    clearSelection();
    toast.success(`Archived ${successCount} lead${successCount !== 1 ? 's' : ''}`);
  }

  // Initialize draft editor when selecting a lead with existing draft
  useEffect(() => {
    if (selectedLead?.email_draft) {
      setDraftSubject(selectedLead.email_draft.subject);
      setDraftBody(selectedLead.email_draft.body);
      setDraftTone(selectedLead.email_draft.tone);
      setDraftId(selectedLead.email_draft.id);
      // Store original draft for style learning comparison
      setOriginalDraft({
        subject: selectedLead.email_draft.subject,
        body: selectedLead.email_draft.body,
      });
    } else {
      setDraftSubject('');
      setDraftBody('');
      setDraftTone('warm');
      setDraftId(null);
      setOriginalDraft(null);
    }
    setIsEditingDraft(false);
    // Reset contact editing state
    setIsEditingEmail(false);
    setIsEditingPhone(false);
  }, [selectedLead?.id]);

  useEffect(() => {
    setCopiedSms(false);
    if (!selectedLead) {
      setSmsDraftBody('');
      return;
    }
    if (leadHasCallablePhone(selectedLead)) {
      setSmsDraftBody(defaultSmsBody(selectedLead, userProfile));
    } else {
      setSmsDraftBody('');
    }
  }, [
    selectedLead?.id,
    selectedLead?.phone,
    selectedLead?.enrichment_data,
    userProfile?.current_title,
    userProfile?.city,
    userProfile?.location,
  ]);

  /** Client-side template (matches app defaults). Avoids stale edge-function drafts and long legacy AI bodies. */
  async function handleGenerateDraft(variant: 'standard' | 'coffee' = 'standard') {
    if (!user || !selectedLead) return;

    setIsGenerating(true);
    setGenerationError(null);

    try {
      const profile = userProfile ?? (await getProfile(user.uid));
      if (!userProfile && profile) setUserProfile(profile);

      const categorized = categorizeLeadOutreach(selectedLead, profile);
      setCommonGround(leadCommonGroundToUi(categorized.commonGround || []));
      setSameCity(
        categorized.outreachType === 'local' ||
          (categorized.commonGround ?? []).some((c) => c.type === 'city' || c.type === 'metro')
      );

      const tone: EmailTone = 'warm';
      const subject =
        variant === 'coffee'
          ? defaultOutreachCoffeeSubject(profile)
          : defaultOutreachSubject(profile);
      const rawBase =
        variant === 'coffee'
          ? defaultOutreachCoffeeBody(selectedLead, profile)
          : defaultOutreachBody(selectedLead, profile);
      const baseBody = collapseOutreachParagraph(rawBase);

      // Gmail API drafts are raw MIME: Gmail does not inject "Insert signature" from compose. Merge profile
      // signature + primary send-as signature (needs gmail.readonly on the Google token).
      let gmailSignaturePlain: string | null = null;
      if (gmailAccessToken) {
        gmailSignaturePlain = await getGmailSendAsSignaturePlain(gmailAccessToken);
      }
      const body = mergeEmailSignaturesForDraft(baseBody, profile, gmailSignaturePlain);

      const { id } = await saveEmailDraft(user.uid, selectedLead.id, { subject, body, tone });
      setDraftSubject(subject);
      setDraftBody(body);
      setDraftTone(tone);
      setDraftId(id);
      setOriginalDraft({ subject, body });

      if (gmailAccessToken && selectedLead.email && !selectedLead.email.includes('@apollo.local')) {
        const gmailResult = await createGmailDraft(gmailAccessToken, selectedLead.email, subject, body);
        if (gmailResult.success) {
          toast.success('Draft created in Gmail');
          startGmailSentWatch(selectedLead.id);
        } else {
          console.warn('Gmail draft creation failed:', gmailResult.error);
        }
      }

      setLeads((prev) =>
        prev.map((l) =>
          l.id === selectedLead.id
            ? { ...l, email_draft: { id, subject, body, tone, is_current: true } as any }
            : l
        )
      );
      setSelectedLead((prev) =>
        prev ? { ...prev, email_draft: { id, subject, body, tone, is_current: true } as any } : null
      );
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      setGenerationError(`Failed to generate: ${msg}`);
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleSaveDraft() {
    if (!user || !selectedLead) return;

    setIsSavingDraft(true);
    try {
      if (draftId) {
        // Update existing draft
        await updateEmailDraft(draftId, {
          subject: draftSubject,
          body: draftBody,
          tone: draftTone,
        });
      } else {
        // Create new draft
        const { id } = await saveEmailDraft(user.uid, selectedLead.id, {
          subject: draftSubject,
          body: draftBody,
          tone: draftTone,
        });
        setDraftId(id);
      }

      // Style Learning (S3-4): Learn from user edits
      // Compare original draft with edited version to capture style preferences
      if (originalDraft && (originalDraft.subject !== draftSubject || originalDraft.body !== draftBody)) {
        learnStyleFromEdit(
          user.uid,
          originalDraft,
          { subject: draftSubject, body: draftBody },
          selectedLead.id
        ).then((result) => {
          if (result.success && result.patternsLearned > 0) {
            toast.info('Style learned', 'Your writing preferences have been saved');
          }
        }).catch((err) => {
          // Don't block on style learning errors
          console.error('Style learning error:', err);
        });
      }

      // Update the lead in the list
      setLeads((prev) =>
        prev.map((l) =>
          l.id === selectedLead.id
            ? { ...l, email_draft: { id: draftId, subject: draftSubject, body: draftBody, tone: draftTone, is_current: true } as any }
            : l
        )
      );

      setSelectedLead((prev) =>
        prev ? { ...prev, email_draft: { id: draftId, subject: draftSubject, body: draftBody, tone: draftTone, is_current: true } as any } : null
      );

      // Update original draft to the new saved version
      setOriginalDraft({ subject: draftSubject, body: draftBody });

      setIsEditingDraft(false);
      toast.success('Draft saved', 'Your changes have been saved');
    } catch (error) {
      console.error('Failed to save draft:', error);
      toast.error('Failed to save draft', 'Please try again');
    } finally {
      setIsSavingDraft(false);
    }
  }

  function handleCancelEdit() {
    if (selectedLead?.email_draft) {
      setDraftSubject(selectedLead.email_draft.subject);
      setDraftBody(selectedLead.email_draft.body);
      setDraftTone(selectedLead.email_draft.tone);
    }
    setIsEditingDraft(false);
  }

  function toggleFilter<T>(array: T[], value: T): T[] {
    return array.includes(value) ? array.filter((v) => v !== value) : [...array, value];
  }

  function clearFilters() {
    setFilters({ search: '', scores: [], industries: [], signals: [] });
  }

  async function handleToggleStar(leadId: string, currentStarred: boolean) {
    try {
      await toggleLeadStarred(leadId, !currentStarred);
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, is_starred: !currentStarred } : l))
      );
      if (selectedLead?.id === leadId) {
        setSelectedLead((prev) => (prev ? { ...prev, is_starred: !currentStarred } : prev));
      }
    } catch (error) {
      console.error('Failed to toggle star:', error);
    }
  }

  const filteredLeads = useMemo(() => {
    let result = leads;

    // Status / starred / locals filter
    if (activeTab === 'starred') {
      result = result.filter((l) => l.is_starred);
    } else if (activeTab === 'locals') {
      result = result.filter(
        (l) => l.status !== 'archived' && leadMatchesUserLocal(userProfile, l)
      );
      if (localsSubTab === 'sent') {
        result = result.filter(
          (l) => l.status === 'contacted' || l.status === 'replied' || l.status === 'converted'
        );
      } else if (localsSubTab === 'pending') {
        result = result.filter(
          (l) => isLocalsNotYetSentStatus(l.status) && !leadNeedsEmailResearch(l)
        );
      } else {
        result = result.filter((l) => isLocalsNotYetSentStatus(l.status) && leadNeedsEmailResearch(l));
      }
    } else if (activeTab === 'phones') {
      result = result.filter((l) => l.status !== 'archived' && leadHasCallablePhone(l));
    } else if (activeTab === 'all') {
      // Exclude archived leads from "All" view - they only show in "Archived" tab
      result = result.filter((l) => l.status !== 'archived');
    } else {
      result = result.filter((l) => l.status === activeTab);
    }

    // Search filter (include location so Locals tab + city search works)
    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter((l) => {
        const loc = getEffectiveLeadLocation(l).toLowerCase();
        const phoneStr = (getLeadPhoneForUi(l) || l.phone || '').toLowerCase();
        const phoneDigits = getLeadPhoneDigits(l);
        const qDigits = q.replace(/\D/g, '');
        return (
          l.first_name.toLowerCase().includes(q) ||
          l.last_name.toLowerCase().includes(q) ||
          l.email.toLowerCase().includes(q) ||
          l.company_name.toLowerCase().includes(q) ||
          l.title.toLowerCase().includes(q) ||
          loc.includes(q) ||
          phoneStr.includes(q) ||
          (qDigits.length >= 3 && phoneDigits.includes(qDigits))
        );
      });
    }

    // Score filter
    if (filters.scores.length > 0) {
      result = result.filter((l) => filters.scores.includes(l.score));
    }

    // Industry filter
    if (filters.industries.length > 0) {
      result = result.filter((l) => filters.industries.includes(l.company_industry));
    }

    // Signal filter
    if (filters.signals.length > 0) {
      result = result.filter((l) => l.signal_type && filters.signals.includes(l.signal_type));
    }

    return result;
  }, [leads, activeTab, localsSubTab, filters, userProfile]);

  const localsSubTabCounts = useMemo(() => {
    if (activeTab !== 'locals') return null;
    const base = leads.filter(
      (l) => l.status !== 'archived' && leadMatchesUserLocal(userProfile, l)
    );
    return {
      sent: base.filter(
        (l) => l.status === 'contacted' || l.status === 'replied' || l.status === 'converted'
      ).length,
      pending: base.filter(
        (l) => isLocalsNotYetSentStatus(l.status) && !leadNeedsEmailResearch(l)
      ).length,
      research: base.filter(
        (l) => isLocalsNotYetSentStatus(l.status) && leadNeedsEmailResearch(l)
      ).length,
    };
  }, [leads, activeTab, userProfile]);

  // Group leads by outreach type
  const groupedLeads = useMemo(() => {
    const categorized = filteredLeads.map((lead) => categorizeLeadOutreach(lead, userProfile));

    const groups: Record<OutreachType, LeadWithOutreach[]> = {
      local: [],
      connected: [],
      other: [],
    };

    categorized.forEach((lead) => {
      groups[lead.outreachType].push(lead);
    });

    return groups;
  }, [filteredLeads, userProfile]);

  // Section config for display
  const SECTION_CONFIG: Record<OutreachType, { label: string; icon: string; description: string; bgColor: string; borderColor: string; iconColor: string }> = {
    local: {
      label: 'Locals',
      icon: '📍',
      description: 'Same city or metro as your profile — good for in-person touchpoints',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/30',
      iconColor: 'text-amber-400',
    },
    connected: {
      label: 'Shared Background',
      icon: '🤝',
      description: 'Shared schools, companies, or industry',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
      iconColor: 'text-green-400',
    },
    other: {
      label: 'Cold outreach',
      icon: '📧',
      description: 'No local tie-in or shared school/company detected — still good targets; personalize from role and company',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      iconColor: 'text-blue-400',
    },
  };

  function toggleSection(type: OutreachType) {
    setCollapsedSections((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  }

  // Save contact info (email/phone) to database
  async function saveContactInfo(field: 'email' | 'phone', value: string) {
    if (!selectedLead) return;

    try {
      await updateLeadContactInfo(selectedLead.id, field, value || null);

      // Update local state
      const updates = { [field]: value || null };
      setLeads((prev) =>
        prev.map((l) => (l.id === selectedLead.id ? { ...l, ...updates } : l))
      );
      setSelectedLead((prev) => (prev ? { ...prev, ...updates } : prev));
      toast.success(`${field === 'email' ? 'Email' : 'Phone'} updated`);
    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
      toast.error(`Failed to update ${field}`, 'Please try again');
    }
  }

  async function handlePipelineStatusChange(leadId: string, nextStatus: LeadStatus) {
    if (selectedLead?.id === leadId && selectedLead.status === nextStatus) return;
    await applyLeadPipelineStatus(leadId, nextStatus);
  }

  async function handleLocalOverrideChange(leadId: string, value: LeadLocalOverride) {
    const lead = leads.find((l) => l.id === leadId) ?? selectedLead;
    if (!lead || getLocalOverride(lead) === value) return;
    try {
      await updateLeadLocalOverride(leadId, lead.enrichment_data as Record<string, unknown> | null, value);
      const nextEd: Record<string, unknown> = { ...(lead.enrichment_data || {}) };
      if (value === 'auto') {
        delete nextEd.tractionbee_local_override;
      } else {
        nextEd.tractionbee_local_override = value;
      }
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, enrichment_data: nextEd } : l))
      );
      setSelectedLead((prev) => (prev?.id === leadId ? { ...prev, enrichment_data: nextEd } : prev));
      toast.success('Area setting saved');
    } catch (error) {
      console.error('Failed to update local override:', error);
      toast.error('Could not save area setting', 'Try again');
    }
  }

  // Bulk selection handlers
  function toggleLeadSelection(leadId: string) {
    setSelectedLeads((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  }

  function clearSelection() {
    setSelectedLeads(new Set());
  }

  function handleDownloadSelectedCsv() {
    const rows = leads.filter((l) => selectedLeads.has(l.id));
    if (rows.length === 0) {
      toast.info('No contacts', 'Select at least one lead in the list.');
      return;
    }
    const stamp = new Date().toISOString().slice(0, 10);
    downloadLeadsCsv(rows, `tractionbee-leads-${stamp}.csv`);
    toast.success(`Downloaded ${rows.length} contact${rows.length !== 1 ? 's' : ''} as CSV`);
  }

  // Bulk enrich selected leads
  async function handleBulkEnrich() {
    if (!user || selectedLeads.size === 0) return;

    const leadsToEnrich = leads.filter(
      (l) => selectedLeads.has(l.id) && leadNeedsEmailEnrichment(l)
    );

    if (leadsToEnrich.length === 0) {
      toast.info(
        'No leads to enrich',
        'Select pending leads that still need a real email (including @apollo.local placeholders).'
      );
      return;
    }

    setIsBulkEnriching(true);
    setBulkEnrichProgress({ current: 0, total: leadsToEnrich.length });

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < leadsToEnrich.length; i++) {
      const lead = leadsToEnrich[i];
      setBulkEnrichProgress({ current: i + 1, total: leadsToEnrich.length });

      try {
        const result = await revealLeadEmail(user.uid, lead.id);
        if (result.success) {
          // Update local state with all enriched fields
          setLeads((prev) =>
            prev.map((l) =>
              l.id === lead.id
                ? {
                    ...l,
                    email: result.email || l.email,
                    first_name: result.first_name || l.first_name,
                    last_name: result.last_name || l.last_name,
                    photo_url: result.photo_url || l.photo_url,
                    location: result.location || l.location,
                    linkedin_url: result.linkedin_url || l.linkedin_url,
                    phone: result.phone || l.phone,
                  }
                : l
            )
          );
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        console.error(`Failed to enrich lead ${lead.id}:`, error);
        failCount++;
      }

      // Small delay between requests to avoid rate limiting
      if (i < leadsToEnrich.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }

    setIsBulkEnriching(false);
    setBulkEnrichProgress({ current: 0, total: 0 });
    clearSelection();

    if (successCount > 0) {
      toast.success(
        `Enriched ${successCount} lead${successCount !== 1 ? 's' : ''}`,
        failCount > 0 ? `${failCount} failed` : undefined
      );
    } else {
      toast.error('Enrichment failed', 'Could not enrich any selected leads');
    }
  }

  // Get unique industries from leads
  const availableIndustries = useMemo(() => {
    const industries = new Set(leads.map((l) => l.company_industry).filter(Boolean));
    return Array.from(industries).sort();
  }, [leads]);

  return (
    <div className="flex h-screen flex-col lg:flex-row">
      {/* Lead list - always visible on desktop, hidden on mobile when lead selected */}
      <div className={classNames(
        "flex-1 overflow-auto border-r border-cacao-dark-tertiary",
        selectedLead ? "hidden lg:block" : "block"
      )}>
        <div className="sticky top-0 border-b border-cacao-dark-tertiary bg-cacao-dark p-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cacao-muted" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                placeholder={
                  activeTab === 'locals'
                    ? 'Search name, company, title, or city…'
                    : activeTab === 'phones'
                      ? 'Search name, company, title, or phone…'
                      : 'Search leads…'
                }
                className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary py-2 pl-9 pr-3 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={classNames(
                'flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-colors',
                showFilters || activeFilterCount > 0
                  ? 'border-cacao-gold text-cacao-gold'
                  : 'border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold hover:text-white'
              )}
            >
              <Filter className="h-3.5 w-3.5" />
              Filter
              {activeFilterCount > 0 && (
                <span className="rounded-full bg-cacao-gold px-1.5 text-[10px] font-semibold text-cacao-dark">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <Link
              to={ROUTES.DO_NOT_CONTACT}
              className="flex items-center gap-2 rounded-md border border-cacao-dark-tertiary px-3 py-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
            >
              <Ban className="h-3.5 w-3.5" />
              DNC List
            </Link>
            <button
              onClick={handleSourceLeads}
              disabled={isSourcingLeads}
              className="flex items-center gap-2 rounded-md bg-cacao-gold px-3 py-2 text-xs font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors disabled:opacity-50"
            >
              {isSourcingLeads ? (
                <>
                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-cacao-dark border-t-transparent" />
                  Sourcing...
                </>
              ) : (
                <>
                  <Rocket className="h-3.5 w-3.5" />
                  Source Leads
                </>
              )}
            </button>
            <Link
              to={ROUTES.LEADS_IMPORT}
              className="flex items-center gap-2 rounded-md border border-cacao-dark-tertiary px-3 py-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
            >
              <Upload className="h-3.5 w-3.5" />
              Import
            </Link>
          </div>

          {/* Bulk Action Bar */}
          {selectedLeads.size > 0 && (
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg bg-cacao-gold/10 border border-cacao-gold/30 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-cacao-gold">
                  {selectedLeads.size} selected
                </span>
                <button
                  onClick={clearSelection}
                  className="text-xs text-cacao-muted hover:text-white transition-colors"
                >
                  Clear
                </button>
              </div>
              <div className="flex items-center gap-2">
                {isBulkEnriching ? (
                  <div className="flex items-center gap-2 text-sm text-cacao-gold">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-cacao-gold border-t-transparent" />
                    <span className="hidden sm:inline">Enriching</span> {bulkEnrichProgress.current}/{bulkEnrichProgress.total}
                  </div>
                ) : (
                  <button
                    onClick={handleBulkEnrich}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 rounded-md bg-cacao-gold px-3 py-2 text-xs font-semibold text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Enrich profiles
                  </button>
                )}
                <button
                  onClick={handleDownloadSelectedCsv}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 rounded-md border border-cacao-dark-tertiary px-3 py-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download CSV
                </button>
                <button
                  onClick={handleBulkArchive}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 rounded-md border border-cacao-dark-tertiary px-3 py-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
                >
                  <Archive className="h-3.5 w-3.5" />
                  Archive
                </button>
              </div>
            </div>
          )}

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-3 rounded-md border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-medium text-[#F5F5F5]">Filters</h3>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-cacao-muted hover:text-white transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className="mt-3 space-y-3">
                {/* Score filter */}
                <div>
                  <label className="block text-xs text-cacao-muted mb-1.5">Score</label>
                  <div className="flex flex-wrap gap-2">
                    {SCORE_OPTIONS.map((score) => (
                      <button
                        key={score}
                        onClick={() => setFilters((f) => ({ ...f, scores: toggleFilter(f.scores, score) }))}
                        className={classNames(
                          'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                          filters.scores.includes(score)
                            ? SCORE_CONFIG[score].className
                            : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold'
                        )}
                      >
                        {SCORE_CONFIG[score].label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Industry filter */}
                <div>
                  <label className="block text-xs text-cacao-muted mb-1.5">Industry</label>
                  <div className="flex flex-wrap gap-2">
                    {availableIndustries.slice(0, 8).map((industry) => (
                      <button
                        key={industry}
                        onClick={() => setFilters((f) => ({ ...f, industries: toggleFilter(f.industries, industry) }))}
                        className={classNames(
                          'rounded-full px-2.5 py-1 text-xs transition-colors',
                          filters.industries.includes(industry)
                            ? 'bg-cacao-gold text-cacao-dark'
                            : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold'
                        )}
                      >
                        {industry}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Signal filter */}
                <div>
                  <label className="block text-xs text-cacao-muted mb-1.5">Signal Type</label>
                  <div className="flex flex-wrap gap-2">
                    {SIGNAL_OPTIONS.map((signal) => (
                      <button
                        key={signal}
                        onClick={() => setFilters((f) => ({ ...f, signals: toggleFilter(f.signals, signal) }))}
                        className={classNames(
                          'rounded-full px-2.5 py-1 text-xs capitalize transition-colors',
                          filters.signals.includes(signal)
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold'
                        )}
                      >
                        {signal.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-2">
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setActiveTab(tab.value)}
                    className={classNames(
                      'flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors',
                      activeTab === tab.value
                        ? 'bg-cacao-gold text-cacao-dark'
                        : 'text-cacao-muted hover:text-white'
                    )}
                  >
                    {tab.icon && <tab.icon className="h-3 w-3" />}
                    {tab.label}
                  </button>
                ))}
              </div>
              <span className="text-xs text-cacao-text-tertiary">
                {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''}
              </span>
            </div>
            {activeTab === 'locals' && localsSubTabCounts && (
              <div className="flex flex-wrap items-center gap-2 border-t border-cacao-dark-tertiary/80 pt-2">
                <span className="text-[10px] font-medium uppercase tracking-wide text-cacao-text-tertiary">
                  Locals
                </span>
                {(
                  [
                    { id: 'sent' as const, label: 'Sent', icon: Send, count: localsSubTabCounts.sent },
                    { id: 'pending' as const, label: 'Pending', icon: Clock, count: localsSubTabCounts.pending },
                    { id: 'research' as const, label: 'Research', icon: Search, count: localsSubTabCounts.research },
                  ]
                ).map(({ id, label, icon: Icon, count }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setLocalsSubTab(id)}
                    className={classNames(
                      'flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
                      localsSubTab === id
                        ? 'bg-amber-500/25 text-amber-200 ring-1 ring-amber-500/40'
                        : 'text-cacao-muted hover:bg-cacao-dark-tertiary hover:text-white'
                    )}
                  >
                    <Icon className="h-3 w-3 opacity-90" />
                    {label}
                    <span
                      className={classNames(
                        'min-w-[1.1rem] rounded-full px-1 text-[10px] tabular-nums',
                        localsSubTab === id ? 'bg-amber-500/20 text-amber-100' : 'bg-cacao-dark-tertiary text-cacao-text-tertiary'
                      )}
                    >
                      {count}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {filteredLeads.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedLeads((prev) => {
                      const visibleIds = filteredLeads.map((l) => l.id);
                      const allOn =
                        visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
                      const next = new Set(prev);
                      if (allOn) {
                        visibleIds.forEach((id) => next.delete(id));
                      } else {
                        visibleIds.forEach((id) => next.add(id));
                      }
                      return next;
                    });
                  }}
                  className="flex items-center gap-2 rounded-md border border-cacao-dark-tertiary px-2.5 py-1 text-[10px] font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
                >
                  {filteredLeads.length > 0 &&
                  filteredLeads.every((l) => selectedLeads.has(l.id)) ? (
                    <CheckSquare className="h-3.5 w-3.5 text-cacao-gold" />
                  ) : (
                    <Square className="h-3.5 w-3.5" />
                  )}
                  Select all in view ({filteredLeads.length})
                </button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">
            {/* Skeleton loading state */}
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-lg bg-cacao-dark-secondary/50 slide-up stagger-${i}`}>
                <div className="h-10 w-10 rounded-full bg-cacao-dark-tertiary shimmer" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 rounded bg-cacao-dark-tertiary shimmer" />
                  <div className="h-3 w-48 rounded bg-cacao-dark-tertiary shimmer" />
                  <div className="h-3 w-24 rounded bg-cacao-dark-tertiary shimmer" />
                </div>
                <div className="h-6 w-16 rounded-full bg-cacao-dark-tertiary shimmer" />
              </div>
            ))}
          </div>
        ) : filteredLeads.length === 0 ? (
          leads.length === 0 ? (
            <div className="py-16 px-6 text-center">
              <div className="relative mx-auto w-32 h-32 mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cacao-gold/20 to-transparent pulse-glow" />
                <div className="absolute inset-4 rounded-full bg-gradient-to-br from-cacao-dark-secondary to-cacao-dark-tertiary flex items-center justify-center animate-float">
                  <Rocket className="h-12 w-12 text-cacao-gold" />
                </div>
                <div className="absolute top-2 right-4 h-2 w-2 rounded-full bg-cacao-gold/60 animate-pulse" />
                <div className="absolute bottom-6 left-2 h-1.5 w-1.5 rounded-full bg-green-400/60 animate-pulse" style={{ animationDelay: '0.5s' }} />
                <div className="absolute top-1/2 right-0 h-1 w-1 rounded-full bg-blue-400/60 animate-pulse" style={{ animationDelay: '1s' }} />
              </div>

              <h3 className="text-lg font-semibold text-[#F5F5F5]">Your Pipeline Awaits</h3>
              <p className="mt-2 text-sm text-cacao-muted max-w-sm mx-auto">
                Source your first batch of leads to start building meaningful business relationships.
              </p>

              <div className="mt-6 flex justify-center gap-4 text-xs text-cacao-muted">
                <span className="flex items-center gap-1">
                  <Target className="h-3.5 w-3.5 text-green-400" />
                  AI-targeted
                </span>
                <span className="flex items-center gap-1">
                  <Zap className="h-3.5 w-3.5 text-amber-400" />
                  Signal-based
                </span>
                <span className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5 text-blue-400" />
                  Quality leads
                </span>
              </div>

              <button
                onClick={handleSourceLeads}
                disabled={isSourcingLeads}
                className="mt-8 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-cacao-gold to-cacao-gold-hover px-6 py-3 text-sm font-semibold text-cacao-dark hover:shadow-lg hover:shadow-cacao-gold/25 transition-all duration-200 disabled:opacity-50 pulse-glow"
              >
                {isSourcingLeads ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-cacao-dark border-t-transparent" />
                    Finding Your Ideal Prospects...
                  </>
                ) : (
                  <>
                    <Rocket className="h-4 w-4" />
                    Source Your First Leads
                  </>
                )}
              </button>

              <p className="mt-4 text-[10px] text-cacao-text-tertiary">
                We'll find prospects that match your ideal customer profile
              </p>
            </div>
          ) : activeTab === 'phones' ? (
            <div className="py-14 px-6 text-center max-w-md mx-auto">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10">
                <Phone className="h-7 w-7 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-[#F5F5F5]">No leads with a phone yet</h3>
              <p className="mt-3 text-sm text-cacao-muted leading-relaxed">
                Use <span className="font-medium text-cacao-gold">Enrich profile</span> on a lead (Apollo can reveal
                mobile numbers when your plan includes it), or add a number manually. Archived leads stay out of this
                tab so you can focus on people you can still reach.
              </p>
              <p className="mt-4 text-xs text-cacao-text-tertiary">
                Once a lead has at least 10 digits on file, they show up here for SMS and quick follow-up.
              </p>
              <button
                type="button"
                onClick={() => setActiveTab('pending')}
                className="mt-6 rounded-lg border border-cacao-dark-tertiary px-4 py-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
              >
                Back to Pending
              </button>
            </div>
          ) : activeTab === 'locals' ? (
            <div className="py-14 px-6 text-center max-w-md mx-auto">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10">
                {localsSubTab === 'sent' ? (
                  <Send className="h-7 w-7 text-amber-400" />
                ) : localsSubTab === 'research' ? (
                  <Search className="h-7 w-7 text-amber-400" />
                ) : (
                  <MapPin className="h-7 w-7 text-amber-400" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-[#F5F5F5]">
                {localsSubTab === 'sent'
                  ? 'No sent locals yet'
                  : localsSubTab === 'research'
                    ? 'No locals need research'
                    : 'No local matches in this view'}
              </h3>
              <p className="mt-3 text-sm text-cacao-muted leading-relaxed">
                {localsSubTab === 'sent' ? (
                  <>
                    When you mark local leads as Sent (or they reply), they appear here. Try the{' '}
                    <button
                      type="button"
                      onClick={() => setLocalsSubTab('pending')}
                      className="font-medium text-cacao-gold underline hover:text-cacao-gold-hover"
                    >
                      Pending
                    </button>{' '}
                    tab for locals ready to email.
                  </>
                ) : localsSubTab === 'research' ? (
                  <>
                    Everyone local either has a real email (see the{' '}
                    <button
                      type="button"
                      onClick={() => setLocalsSubTab('pending')}
                      className="font-medium text-cacao-gold underline hover:text-cacao-gold-hover"
                    >
                      Pending
                    </button>{' '}
                    tab) or hasn&apos;t matched your area yet. Use{' '}
                    <span className="font-medium text-cacao-gold">Enrich profiles</span> on pending locals missing an address, or add email manually.
                  </>
                ) : getUserLocationCandidates(userProfile).length === 0 ? (
                  <>
                    Add your{' '}
                    <Link to="/dashboard/profile" className="text-cacao-gold underline hover:text-cacao-gold-hover">
                      city
                    </Link>{' '}
                    (home metro, e.g. Los Angeles) on your profile so we can compare to each lead&apos;s city.
                  </>
                ) : (
                  <>
                    Leads need a city on file to match your area. Use{' '}
                    <span className="font-medium text-cacao-gold">Select all in view</span> then{' '}
                    <span className="font-medium text-cacao-gold">Enrich</span> on pending leads to pull location from
                    Apollo, or source leads in your target geography. If your{' '}
                    <span className="font-medium text-cacao-muted">Location</span> is a street address, set{' '}
                    <span className="font-medium text-cacao-muted">City</span> to your metro—we check both.
                  </>
                )}
              </p>
              <p className="mt-4 text-xs text-cacao-text-tertiary">
                Search by city, name, or company in the box above while on Locals. Lead Criteria geography is included in matching.
              </p>
              <button
                type="button"
                onClick={() => setActiveTab('pending')}
                className="mt-6 rounded-lg border border-cacao-dark-tertiary px-4 py-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
              >
                Back to Pending
              </button>
            </div>
          ) : activeTab === 'starred' ? (
            <div className="py-14 px-6 text-center max-w-sm mx-auto">
              <Star className="h-10 w-10 text-cacao-gold mx-auto mb-4 opacity-80" />
              <h3 className="text-lg font-semibold text-[#F5F5F5]">No starred leads</h3>
              <p className="mt-2 text-sm text-cacao-muted">
                Star leads from the list to collect them here.
              </p>
            </div>
          ) : (
            <div className="py-14 px-6 text-center max-w-sm mx-auto">
              <Search className="h-10 w-10 text-cacao-muted mx-auto mb-4 opacity-60" />
              <h3 className="text-lg font-semibold text-[#F5F5F5]">No leads match</h3>
              <p className="mt-2 text-sm text-cacao-muted">
                Try another tab, clearing the search box, or resetting filters.
              </p>
              {(filters.search || activeFilterCount > 0) && (
                <button
                  type="button"
                  onClick={() => {
                    clearFilters();
                    setFilters((f) => ({ ...f, search: '' }));
                  }}
                  className="mt-5 rounded-lg bg-cacao-gold/10 border border-cacao-gold/30 px-4 py-2 text-xs font-medium text-cacao-gold hover:bg-cacao-gold/20 transition-colors"
                >
                  Clear search &amp; filters
                </button>
              )}
            </div>
          )
        ) : (
          <div className="divide-y divide-cacao-dark-tertiary">
            {/* Profile tip when no Local/Connected sections */}
            {activeTab !== 'phones' &&
              groupedLeads.local.length === 0 &&
              groupedLeads.connected.length === 0 &&
              groupedLeads.other.length > 0 && (
              <div className="p-3 bg-amber-500/10 border-b border-amber-500/30">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-amber-400">Find Local & Connected Leads</p>
                    <p className="text-[10px] text-amber-300/70 mt-0.5">
                      Fill in your <Link to="/dashboard/profile" className="underline hover:text-amber-200">profile</Link> (city or location, education, work history) so Locals and shared-background groups populate.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Grouped Sections */}
            {(['local', 'connected', 'other'] as OutreachType[]).map((sectionType) => {
              const sectionLeads = groupedLeads[sectionType];
              const config = SECTION_CONFIG[sectionType];
              const isCollapsed = collapsedSections[sectionType];

              if (sectionLeads.length === 0) return null;

              return (
                <div key={sectionType} className="border-b border-cacao-dark-tertiary">
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(sectionType)}
                    className={classNames(
                      'w-full flex items-center justify-between p-3 hover:bg-cacao-dark-secondary transition-colors',
                      config.bgColor
                    )}
                  >
                    <div className="flex items-center gap-2">
                      {isCollapsed ? (
                        <ChevronRight className="h-4 w-4 text-cacao-muted" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-cacao-muted" />
                      )}
                      <span className="text-lg">{config.icon}</span>
                      <div className="text-left">
                        <span className={classNames('text-sm font-medium', config.iconColor)}>
                          {config.label}
                        </span>
                        <p className="text-[10px] text-cacao-muted">{config.description}</p>
                      </div>
                    </div>
                    <span className={classNames('rounded-full px-2 py-0.5 text-xs font-semibold', config.bgColor, config.iconColor)}>
                      {sectionLeads.length}
                    </span>
                  </button>

                  {/* Section Leads */}
                  {!isCollapsed && (
                    <div className="space-y-1 p-2">
                      {sectionLeads.map((lead) => (
                        <div
                          key={lead.id}
                          className={classNames(
                            'group w-full rounded-lg p-3 text-left transition-all duration-200 flex items-start gap-2',
                            selectedLead?.id === lead.id
                              ? 'bg-cacao-dark-tertiary ring-1 ring-cacao-gold/30'
                              : 'hover:bg-cacao-dark-secondary'
                          )}
                        >
                          {/* Selection Checkbox */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLeadSelection(lead.id);
                            }}
                            className="flex-shrink-0 mt-0.5 p-1 rounded hover:bg-cacao-dark-tertiary transition-colors"
                          >
                            {selectedLeads.has(lead.id) ? (
                              <CheckSquare className="h-4 w-4 text-cacao-gold" />
                            ) : (
                              <Square className="h-4 w-4 text-cacao-muted group-hover:text-cacao-text-secondary" />
                            )}
                          </button>

                          {/* Clickable area for lead details */}
                          <button
                            onClick={() => setSelectedLead(lead)}
                            className="flex-1 text-left"
                          >
                          <div className="flex items-start gap-3">
                            {/* Avatar with score indicator ring */}
                            <div className="relative flex-shrink-0">
                              {lead.photo_url ? (
                                <img
                                  src={lead.photo_url}
                                  alt={`${lead.first_name} ${lead.last_name}`}
                                  className={classNames(
                                    'h-10 w-10 rounded-full object-cover ring-2',
                                    lead.score === 'high'
                                      ? 'ring-green-500/50'
                                      : lead.score === 'medium'
                                      ? 'ring-cacao-gold/50'
                                      : 'ring-cacao-dark-tertiary'
                                  )}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div
                                  className={classNames(
                                    'flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ring-2',
                                    lead.score === 'high'
                                      ? 'bg-green-500/20 text-green-400 ring-green-500/50'
                                      : lead.score === 'medium'
                                      ? 'bg-cacao-gold/20 text-cacao-gold ring-cacao-gold/50'
                                      : 'bg-cacao-dark-tertiary text-cacao-muted ring-cacao-dark-tertiary'
                                  )}
                                >
                                  {lead.first_name[0]}{lead.last_name?.[0] || ''}
                                </div>
                              )}
                              {/* Star badge on avatar */}
                              {lead.is_starred && (
                                <div className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-cacao-gold text-[8px]">
                                  ⭐
                                </div>
                              )}
                            </div>

                            {/* Main content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-[#F5F5F5] truncate">
                                  {lead.first_name} {lead.last_name}
                                </p>
                                {/* Outreach type badge */}
                                <span
                                  className={classNames(
                                    'flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                                    config.bgColor,
                                    config.iconColor
                                  )}
                                  title={lead.outreachReason}
                                >
                                  {lead.outreachIcon} {sectionType === 'local' ? 'Local' : sectionType === 'connected' ? 'Connected' : 'New'}
                                </span>
                              </div>

                              <p className="text-xs text-cacao-text-secondary mt-0.5 truncate">
                                {lead.title}
                              </p>
                              <p className="text-xs font-medium text-cacao-muted truncate">
                                {lead.company_name}
                                {lead.company_size && (
                                  <span className="text-cacao-text-tertiary"> · {lead.company_size}</span>
                                )}
                              </p>
                              {lead.location && (
                                <p className="text-[10px] text-cacao-text-tertiary truncate flex items-center gap-1">
                                  <MapPin className="h-2.5 w-2.5" />
                                  {lead.location}
                                </p>
                              )}
                              {activeTab === 'phones' && getLeadPhoneForUi(lead) && (
                                <p className="text-[10px] text-green-400/90 truncate flex items-center gap-1 mt-0.5">
                                  <Phone className="h-2.5 w-2.5 flex-shrink-0" />
                                  {getLeadPhoneForUi(lead)}
                                </p>
                              )}

                              {/* COMMON GROUND - Show ALL shared connections! */}
                              {lead.commonGround && lead.commonGround.length > 0 && (
                                <div className="mt-2 space-y-1.5">
                                  {/* Show connection score badge if multiple connections */}
                                  {lead.connectionScore > 50 && (
                                    <div className="flex items-center gap-1.5 mb-1">
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-gradient-to-r from-cacao-gold/20 to-amber-500/20 text-cacao-gold font-bold border border-cacao-gold/30">
                                        🔥 Strong Connection
                                      </span>
                                    </div>
                                  )}
                                  {/* Show ALL common ground items */}
                                  {lead.commonGround.map((cg, idx) => (
                                    <div key={idx} className={classNames(
                                      'rounded-md px-2.5 py-1.5 border flex items-center gap-2',
                                      cg.type === 'city' || cg.type === 'metro'
                                        ? 'bg-amber-500/10 border-amber-500/30'
                                        : cg.type === 'school' || cg.type === 'employer'
                                        ? 'bg-green-500/10 border-green-500/30'
                                        : 'bg-blue-500/10 border-blue-500/30'
                                    )}>
                                      <span className="text-base flex-shrink-0">{cg.icon}</span>
                                      <div className="min-w-0 flex-1">
                                        <span className={classNames(
                                          'text-[10px] font-bold uppercase tracking-wide block',
                                          cg.type === 'city' || cg.type === 'metro' ? 'text-amber-400'
                                            : cg.type === 'school' || cg.type === 'employer' ? 'text-green-400'
                                            : 'text-blue-400'
                                        )}>
                                          {cg.label}
                                        </span>
                                        <p className={classNames(
                                          'text-xs truncate',
                                          cg.type === 'city' || cg.type === 'metro' ? 'text-amber-300/80'
                                            : cg.type === 'school' || cg.type === 'employer' ? 'text-green-300/80'
                                            : 'text-blue-300/80'
                                        )}>
                                          {cg.detail}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Meta row with location and signal */}
                              <div className="mt-2 flex items-center gap-3 flex-wrap">
                                {/* Always show location if available */}
                                {lead.location && (
                                  <span className={classNames(
                                    'flex items-center gap-1 text-[11px]',
                                    lead.outreachType === 'local' ? 'text-amber-400 font-medium' : 'text-cacao-muted'
                                  )}>
                                    <MapPin className="h-3 w-3" />
                                    {lead.location}
                                  </span>
                                )}
                                {lead.signal_type && (
                                  <span className="flex items-center gap-1 rounded bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-medium text-blue-400">
                                    <Zap className="h-3 w-3" />
                                    {lead.signal_type.replace('_', ' ')}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Right side - Status and actions */}
                            <div className="flex flex-col items-end gap-2 flex-shrink-0">
                              <span
                                className={classNames(
                                  'rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide',
                                  lead.status === 'pending'
                                    ? 'bg-cacao-gold/20 text-cacao-gold'
                                    : lead.status === 'contacted'
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : lead.status === 'replied'
                                    ? 'bg-green-500/20 text-green-400 animate-pulse'
                                    : 'bg-cacao-dark-tertiary text-cacao-muted'
                                )}
                              >
                                {lead.status === 'contacted' ? 'Sent' : lead.status}
                              </span>

                              {/* Score indicator with visual bar */}
                              <div className="flex flex-col items-end gap-0.5">
                                <div className="flex items-center gap-1.5">
                                  <span className={classNames(
                                    'text-[10px] font-bold uppercase tracking-wide',
                                    lead.score === 'high' ? 'text-green-400' : lead.score === 'medium' ? 'text-cacao-gold' : 'text-cacao-muted'
                                  )}>
                                    {lead.score}
                                  </span>
                                  <div className="flex items-center gap-0.5 h-1.5 w-8 rounded-full bg-cacao-dark-tertiary overflow-hidden">
                                    <div className={classNames(
                                      'h-full rounded-full transition-all duration-500',
                                      lead.score === 'high' ? 'w-full bg-gradient-to-r from-green-400 to-emerald-500' : lead.score === 'medium' ? 'w-2/3 bg-gradient-to-r from-cacao-gold to-amber-500' : 'w-1/3 bg-cacao-muted'
                                    )} />
                                  </div>
                                </div>
                                {/* Connection strength mini-bar */}
                                {lead.connectionScore > 0 && (
                                  <div className="flex items-center gap-1 mt-1">
                                    <span className="text-[9px] text-cacao-muted">Match</span>
                                    <div className="w-6 h-1 rounded-full bg-cacao-dark-tertiary overflow-hidden">
                                      <div
                                        className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-700"
                                        style={{ width: `${Math.min(lead.connectionScore, 100)}%` }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Star button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleStar(lead.id, lead.is_starred);
                                }}
                                className={classNames(
                                  'p-1.5 rounded-full transition-all duration-200',
                                  lead.is_starred
                                    ? 'bg-cacao-gold/20'
                                    : 'opacity-0 group-hover:opacity-100 hover:bg-cacao-dark-tertiary'
                                )}
                                title={lead.is_starred ? 'Remove from starred' : 'Add to starred'}
                              >
                                <Star
                                  className={classNames(
                                    'h-4 w-4 transition-all duration-200',
                                    lead.is_starred
                                      ? 'fill-cacao-gold text-cacao-gold scale-110'
                                      : 'text-cacao-muted hover:text-cacao-gold hover:scale-110'
                                  )}
                                />
                              </button>
                            </div>
                          </div>
                        </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail panel - full screen on mobile, side panel on desktop */}
      <div className={classNames(
        "overflow-auto bg-cacao-dark-secondary p-5",
        selectedLead ? "block w-full lg:w-96" : "hidden lg:block lg:w-96"
      )}>
        {selectedLead ? (
          <div>
            {/* Mobile back button */}
            <button
              onClick={() => setSelectedLead(null)}
              className="mb-4 flex items-center gap-2 text-sm text-cacao-muted hover:text-white lg:hidden"
            >
              <ChevronRight className="h-4 w-4 rotate-180" />
              Back to leads
            </button>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {selectedLead.photo_url ? (
                  <img
                    src={selectedLead.photo_url}
                    alt={`${selectedLead.first_name} ${selectedLead.last_name}`}
                    className="h-10 w-10 rounded-full object-cover border border-cacao-gold/30"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`flex h-10 w-10 items-center justify-center rounded-full bg-cacao-dark-tertiary text-sm font-medium text-[#F5F5F5] ${selectedLead.photo_url ? 'hidden' : ''}`}>
                  {selectedLead.first_name[0]}
                  {selectedLead.last_name?.[0] || ''}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#F5F5F5]">
                    {selectedLead.first_name} {selectedLead.last_name}
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-cacao-muted">{selectedLead.title}</p>
                    {selectedLead.title && getSeniorityFromTitle(selectedLead.title).level !== 'individual' && (
                      <span
                        className={classNames(
                          'rounded px-1.5 py-0.5 text-[10px] font-semibold',
                          getSeniorityFromTitle(selectedLead.title).color,
                          'bg-white/5'
                        )}
                      >
                        {getSeniorityFromTitle(selectedLead.title).label}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleToggleStar(selectedLead.id, selectedLead.is_starred)}
                className="p-2 rounded-md hover:bg-cacao-dark-tertiary transition-colors"
                title={selectedLead.is_starred ? 'Remove from starred' : 'Add to starred'}
              >
                <Star
                  className={classNames(
                    'h-5 w-5',
                    selectedLead.is_starred
                      ? 'fill-cacao-gold text-cacao-gold'
                      : 'text-cacao-muted hover:text-cacao-gold'
                  )}
                />
              </button>
            </div>

            {/* Quick Actions - Google Search & LinkedIn */}
            <div className="mt-4 flex gap-2">
              <a
                href={`https://www.google.com/search?q=${encodeURIComponent(
                  `${selectedLead.first_name} ${selectedLead.last_name}${selectedLead.title ? ` ${selectedLead.title}` : ''} ${selectedLead.company_name}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-cacao-dark-tertiary px-2 py-1.5 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
              >
                <Search className="h-3 w-3" />
                Google
              </a>
              {selectedLead.linkedin_url && (
                <a
                  href={selectedLead.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-cacao-dark-tertiary px-2 py-1.5 text-xs font-medium text-cacao-muted hover:border-blue-500 hover:text-blue-400 transition-colors"
                >
                  <ExternalLink className="h-3 w-3" />
                  LinkedIn
                </a>
              )}
            </div>

            {/* Pipeline + area (Locals tab) */}
            <div className="mt-4 space-y-3 rounded-lg border border-cacao-dark-tertiary bg-cacao-dark/80 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-cacao-text-tertiary">
                Pipeline
              </p>
              <div className="space-y-1">
                <label className="text-[10px] text-cacao-muted" htmlFor="lead-status-select">
                  Status
                </label>
                <select
                  id="lead-status-select"
                  value={selectedLead.status}
                  onChange={(e) =>
                    void handlePipelineStatusChange(selectedLead.id, e.target.value as LeadStatus)
                  }
                  className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark px-2 py-2 text-xs text-[#F5F5F5] focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                >
                  {PIPELINE_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-cacao-muted" htmlFor="lead-local-select">
                  Area
                </label>
                <select
                  id="lead-local-select"
                  value={getLocalOverride(selectedLead)}
                  onChange={(e) =>
                    void handleLocalOverrideChange(
                      selectedLead.id,
                      e.target.value as LeadLocalOverride
                    )
                  }
                  className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark px-2 py-2 text-xs text-[#F5F5F5] focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                >
                  <option value="auto">Auto (match by city)</option>
                  <option value="local">Always local</option>
                  <option value="not_local">Not local</option>
                </select>
                <p className="text-[10px] leading-snug text-cacao-text-tertiary">
                  Controls the Locals tab and local grouping. Auto compares your profile city to theirs.
                </p>
              </div>
            </div>

            {/* Email readiness */}
            <div
              className={classNames(
                'mt-3 flex items-center justify-between gap-2 rounded-md border px-3 py-2.5',
                selectedLead.email && selectedLead.email_verified
                  ? 'border-green-500/25 bg-green-500/5'
                  : selectedLead.email
                    ? 'border-amber-500/25 bg-amber-500/5'
                    : 'border-cacao-dark-tertiary bg-cacao-dark'
              )}
            >
              <div className="flex min-w-0 items-center gap-2">
                {selectedLead.email && selectedLead.email_verified ? (
                  <Mail className="h-4 w-4 flex-shrink-0 text-green-400" />
                ) : selectedLead.email ? (
                  <Mail className="h-4 w-4 flex-shrink-0 text-amber-400" />
                ) : (
                  <Clock className="h-4 w-4 flex-shrink-0 text-cacao-muted" />
                )}
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#F5F5F5]">Email</p>
                  <p className="text-[10px] text-cacao-text-tertiary">
                    {selectedLead.email && selectedLead.email_verified
                      ? 'Verified — good to send'
                      : selectedLead.email
                        ? 'Unverified — check before bulk sends'
                        : 'Use Enrich below when ready'}
                  </p>
                </div>
              </div>
              <span
                className={classNames(
                  'flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                  selectedLead.email && selectedLead.email_verified
                    ? 'bg-green-500/20 text-green-400'
                    : selectedLead.email
                      ? 'bg-amber-500/20 text-amber-300'
                      : 'bg-cacao-dark-tertiary text-cacao-muted'
                )}
              >
                {selectedLead.email && selectedLead.email_verified
                  ? 'Verified'
                  : selectedLead.email
                    ? 'Unverified'
                    : 'Missing'}
              </span>
            </div>

            <details className="group mt-3 rounded-md border border-cacao-dark-tertiary bg-cacao-dark/40 open:bg-cacao-dark/60">
              <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2 text-xs font-medium text-cacao-muted marker:content-none [&::-webkit-details-marker]:hidden">
                <span>Why this lead</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-cacao-text-tertiary transition-transform group-open:rotate-180" />
              </summary>
              <div className="space-y-2 border-t border-cacao-dark-tertiary px-3 pb-3 pt-2">
                {selectedLead.signal_type && (
                  <div className="flex items-start gap-2">
                    <Zap className="mt-0.5 h-3 w-3 flex-shrink-0 text-blue-400" />
                    <div>
                      <span className="text-xs capitalize text-[#F5F5F5]">
                        {selectedLead.signal_type.replace('_', ' ')}
                      </span>
                      {selectedLead.signal_details && (
                        <p className="text-[10px] text-cacao-text-tertiary">{selectedLead.signal_details}</p>
                      )}
                    </div>
                  </div>
                )}
                {selectedLead.company_industry && (
                  <div className="flex items-center gap-2">
                    <Target className="h-3 w-3 flex-shrink-0 text-green-400" />
                    <span className="text-xs text-[#F5F5F5]">Industry: {selectedLead.company_industry}</span>
                  </div>
                )}
                {selectedLead.title && (
                  <div className="flex items-center gap-2">
                    <User
                      className={classNames(
                        'h-3 w-3 flex-shrink-0',
                        getSeniorityFromTitle(selectedLead.title).color
                      )}
                    />
                    <span className="text-xs text-[#F5F5F5]">
                      {getSeniorityFromTitle(selectedLead.title).level === 'executive'
                        ? 'Executive-level contact'
                        : getSeniorityFromTitle(selectedLead.title).level === 'vp'
                          ? 'VP-level decision maker'
                          : getSeniorityFromTitle(selectedLead.title).level === 'director'
                            ? 'Director-level contact'
                            : getSeniorityFromTitle(selectedLead.title).level === 'manager'
                              ? 'Management-level contact'
                              : 'Role match'}
                    </span>
                  </div>
                )}
                {selectedLead.company_size && (
                  <div className="flex items-center gap-2">
                    <Users className="h-3 w-3 flex-shrink-0 text-blue-400" />
                    <span className="text-xs text-[#F5F5F5]">Company size: {selectedLead.company_size}</span>
                  </div>
                )}
                {selectedLead.company_funding_stage && (
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-3 w-3 flex-shrink-0 text-emerald-400" />
                    <span className="text-xs text-[#F5F5F5]">
                      Funding:{' '}
                      <span className="capitalize">
                        {selectedLead.company_funding_stage.replace('_', ' ')}
                      </span>
                    </span>
                  </div>
                )}
                {selectedLead.score && (
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-3 w-3 flex-shrink-0 text-cacao-gold" />
                    <span className="text-xs capitalize text-[#F5F5F5]">Score: {selectedLead.score}</span>
                  </div>
                )}
                {!selectedLead.signal_type &&
                  !selectedLead.company_industry &&
                  !selectedLead.company_size && (
                    <p className="text-xs text-cacao-text-tertiary">Matched your ICP criteria</p>
                  )}
              </div>
            </details>

            {selectedLead.status === 'archived' && (
              <p className="mt-3 text-[10px] text-amber-400/90">
                Archived — set Status back to Pending or Sent to work this lead again.
              </p>
            )}

            <div className="mt-5">
              <h3 className="text-xs font-medium text-cacao-muted">Company</h3>
              <p className="mt-1 text-sm font-medium text-[#F5F5F5]">{selectedLead.company_name}</p>
              {selectedLead.company_industry && (
                <p className="text-xs text-cacao-muted">{selectedLead.company_industry}</p>
              )}
              {selectedLead.company_website && (
                <a
                  href={selectedLead.company_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 flex items-center gap-1 text-xs text-cacao-gold hover:text-cacao-gold-hover transition-colors"
                >
                  Visit website
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>

            {/* Enrich Button - Prominent */}
            <button
              onClick={async () => {
                if (!user) return;
                const result = await revealLeadEmail(user.uid, selectedLead.id);
                if (result.success) {
                  // Update the lead in state with all enriched data
                  const updates: Partial<LeadWithDraft> = {};
                  if (result.email) {
                    updates.email = result.email;
                    (updates as any).email_verified = true;
                  }
                  if (result.first_name) {
                    updates.first_name = result.first_name;
                  }
                  if (result.last_name) {
                    updates.last_name = result.last_name;
                  }
                  if (result.photo_url) {
                    updates.photo_url = result.photo_url;
                  }
                  if (result.location) {
                    updates.location = result.location;
                  }
                  if (result.linkedin_url) {
                    updates.linkedin_url = result.linkedin_url;
                  }
                  if (result.phone) {
                    updates.phone = result.phone;
                  }
                  if (result.company_website) {
                    updates.company_website = result.company_website;
                  }
                  if (result.company_industry) {
                    updates.company_industry = result.company_industry;
                  }

                  if (Object.keys(updates).length > 0) {
                    setLeads((prev) =>
                      prev.map((l) =>
                        l.id === selectedLead.id ? { ...l, ...updates } : l
                      )
                    );
                    setSelectedLead((prev) =>
                      prev ? { ...prev, ...updates } : prev
                    );
                  }

                  // Show user-friendly result message
                  if (result.updates && result.updates.length > 0) {
                    const updateCount = result.updates.length;
                    const hasEmail = result.updates.includes('email');
                    const hasPhoto = result.updates.includes('photo_url');

                    let message = '';
                    const hasPhoneUpd = result.updates.includes('phone');
                    if (hasEmail && hasPhoto) {
                      message = 'Found email, photo, and more details';
                    } else if (hasEmail && hasPhoneUpd) {
                      message = 'Found email, phone, and more details';
                    } else if (hasPhoneUpd && !hasEmail) {
                      message = 'Found phone and additional details';
                    } else if (hasEmail) {
                      message = 'Found email and additional details';
                    } else if (hasPhoto) {
                      message = 'Found photo and additional details';
                    } else {
                      message = `Found ${updateCount} new detail${updateCount > 1 ? 's' : ''}`;
                    }

                    toast.success('Profile Enriched', message);
                  } else {
                    toast.info('Already Up to Date', 'No new information found for this lead');
                  }
                } else {
                  toast.error('Enrichment Failed', result.message || result.error || 'Could not find additional information');
                }
              }}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-cacao-gold px-3 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
              title="Reveal email and mobile (uses Apollo credits), plus photo, LinkedIn, and company details"
            >
              <Sparkles className="h-4 w-4" />
              Enrich Profile
            </button>

            {/* Contact Info Section */}
            <div className="mt-4 space-y-2">
              {/* Email - Editable */}
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-cacao-muted flex-shrink-0" />
                {isEditingEmail ? (
                  <div className="flex flex-1 items-center gap-1">
                    <input
                      type="email"
                      value={editingEmail}
                      onChange={(e) => setEditingEmail(e.target.value)}
                      className="flex-1 rounded border border-cacao-dark-tertiary bg-cacao-dark px-2 py-1 text-sm text-[#F5F5F5] focus:border-cacao-gold focus:outline-none"
                      placeholder="email@example.com"
                      autoFocus
                    />
                    <button
                      onClick={() => {
                        saveContactInfo('email', editingEmail);
                        setIsEditingEmail(false);
                      }}
                      className="p-1 rounded hover:bg-green-500/20 text-green-400"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingEmail(false);
                        setEditingEmail(selectedLead.email || '');
                      }}
                      className="p-1 rounded hover:bg-red-500/20 text-red-400"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-1 items-center justify-between">
                    {selectedLead.email && !selectedLead.email.includes('@apollo.local') ? (
                      <>
                        <a
                          href={`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(selectedLead.email)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-cacao-gold hover:text-cacao-gold-hover transition-colors"
                        >
                          {selectedLead.email}
                        </a>
                        <button
                          onClick={() => {
                            setEditingEmail(selectedLead.email || '');
                            setIsEditingEmail(true);
                          }}
                          className="p-1 rounded hover:bg-cacao-dark-tertiary text-cacao-muted hover:text-white"
                          title="Edit email"
                        >
                          <Edit3 className="h-3 w-3" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingEmail('');
                          setIsEditingEmail(true);
                        }}
                        className="flex items-center gap-1 text-xs text-cacao-gold hover:text-cacao-gold-hover transition-colors"
                      >
                        <Plus className="h-3 w-3" />
                        Add email
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Phone with SMS - Editable */}
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-cacao-muted flex-shrink-0" />
                {isEditingPhone ? (
                  <div className="flex flex-1 items-center gap-1">
                    <input
                      type="tel"
                      value={editingPhone}
                      onChange={(e) => setEditingPhone(e.target.value)}
                      className="flex-1 rounded border border-cacao-dark-tertiary bg-cacao-dark px-2 py-1 text-sm text-[#F5F5F5] focus:border-cacao-gold focus:outline-none"
                      placeholder="+1 (555) 000-0000"
                      autoFocus
                    />
                    <button
                      onClick={() => {
                        saveContactInfo('phone', editingPhone);
                        setIsEditingPhone(false);
                      }}
                      className="p-1 rounded hover:bg-green-500/20 text-green-400"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingPhone(false);
                        setEditingPhone(getLeadPhoneForUi(selectedLead) || '');
                      }}
                      className="p-1 rounded hover:bg-red-500/20 text-red-400"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-1 items-center justify-between">
                    {getLeadPhoneForUi(selectedLead) ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#F5F5F5]">{getLeadPhoneForUi(selectedLead)}</span>
                        </div>
                        <button
                          onClick={() => {
                            setEditingPhone(getLeadPhoneForUi(selectedLead) || '');
                            setIsEditingPhone(true);
                          }}
                          className="p-1 rounded hover:bg-cacao-dark-tertiary text-cacao-muted hover:text-white"
                          title="Edit phone"
                        >
                          <Edit3 className="h-3 w-3" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingPhone('');
                          setIsEditingPhone(true);
                        }}
                        className="flex items-center gap-1 text-xs text-cacao-gold hover:text-cacao-gold-hover transition-colors"
                      >
                        <Plus className="h-3 w-3" />
                        Add phone
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Draft Message Section */}
            <div className="mt-5">
              <h3 className="text-xs font-medium text-cacao-muted">Draft Message</h3>

              {!hasDraft ? (
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => handleGenerateDraft('standard')}
                    disabled={isGenerating}
                    className="flex flex-1 items-center justify-center gap-2 rounded-md border border-dashed border-cacao-dark-tertiary bg-cacao-dark py-3 px-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors disabled:opacity-50"
                  >
                    {isGenerating ? (
                      <>
                        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-cacao-muted border-t-cacao-gold" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-3.5 w-3.5" />
                        Generate message
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleGenerateDraft('coffee')}
                    disabled={isGenerating}
                    title="Adds a casual Friday around 9am coffee invite (same area or between two cities when we know both)"
                    className="flex flex-1 items-center justify-center gap-2 rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 py-3 px-2 text-xs font-medium text-amber-200/90 hover:border-amber-400 hover:text-amber-100 transition-colors disabled:opacity-50"
                  >
                    <Coffee className="h-3.5 w-3.5" />
                    Friday coffee line
                  </button>
                </div>
              ) : isEditingDraft ? (
                // Editing mode
                <div className="mt-2 space-y-3">
                  <div>
                    <label className="block text-[10px] font-medium text-cacao-text-tertiary mb-1">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={draftSubject}
                      onChange={(e) => setDraftSubject(e.target.value)}
                      className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                      placeholder="Email subject..."
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-cacao-text-tertiary mb-1">
                      Body
                    </label>
                    <textarea
                      value={draftBody}
                      onChange={(e) => setDraftBody(e.target.value)}
                      rows={10}
                      className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark px-3 py-2 text-xs text-[#F5F5F5] placeholder-cacao-text-tertiary leading-relaxed focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                      placeholder="Email body..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveDraft}
                      disabled={isSavingDraft || !draftSubject.trim() || !draftBody.trim()}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-cacao-gold px-3 py-2 text-xs font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors disabled:opacity-50"
                    >
                      {isSavingDraft ? (
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-cacao-dark border-t-transparent" />
                      ) : (
                        <Check className="h-3 w-3" />
                      )}
                      Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-cacao-dark-tertiary px-3 py-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
                    >
                      <X className="h-3 w-3" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View mode
                <div className="mt-2">
                  <div className="rounded-md border border-cacao-dark-tertiary bg-cacao-dark p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-[#F5F5F5]">{draftSubject}</p>
                      <span
                        className={classNames(
                          'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                          draftTone === 'warm'
                            ? 'bg-orange-500/20 text-orange-400'
                            : 'bg-blue-500/20 text-blue-400'
                        )}
                      >
                        {draftTone}
                      </span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-xs text-cacao-muted leading-relaxed">
                      {draftBody}
                    </p>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <button
                      type="button"
                      onClick={() => setIsEditingDraft(true)}
                      className="flex items-center justify-center gap-1.5 rounded-md border border-cacao-dark-tertiary px-2 py-1.5 text-[10px] font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
                    >
                      <Edit3 className="h-3 w-3" />
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        const fullEmail = `Subject: ${draftSubject}\n\n${draftBody}`;
                        await navigator.clipboard.writeText(fullEmail);
                        setCopiedDraft(true);
                        setTimeout(() => setCopiedDraft(false), 2000);
                      }}
                      className={classNames(
                        'flex items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[10px] font-medium transition-colors',
                        copiedDraft
                          ? 'border-green-500 text-green-400 bg-green-500/10'
                          : 'border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold hover:text-white'
                      )}
                    >
                      {copiedDraft ? (
                        <>
                          <Check className="h-3 w-3" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3" />
                          Copy
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleGenerateDraft('standard')}
                      disabled={isGenerating}
                      className="flex items-center justify-center gap-1.5 rounded-md border border-cacao-dark-tertiary px-2 py-1.5 text-[10px] font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors disabled:opacity-50"
                    >
                      {isGenerating ? (
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-cacao-muted border-t-cacao-gold" />
                      ) : (
                        <RefreshCw className="h-3 w-3" />
                      )}
                      {isGenerating ? 'Writing...' : 'Rewrite'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleGenerateDraft('coffee')}
                      disabled={isGenerating}
                      title="Replace draft with default + Friday coffee line"
                      className="flex items-center justify-center gap-1.5 rounded-md border border-amber-500/35 px-2 py-1.5 text-[10px] font-medium text-amber-200/90 hover:border-amber-400 hover:text-amber-100 transition-colors disabled:opacity-50"
                    >
                      <Coffee className="h-3 w-3" />
                      Coffee
                    </button>
                  </div>

                  {/* Common Ground Section */}
                  {commonGround.length > 0 && (
                    <div className="mt-3 rounded-md border border-green-500/30 bg-green-500/10 p-3">
                      <h4 className="flex items-center gap-1.5 text-[10px] font-semibold text-green-400 uppercase tracking-wide mb-2">
                        <Users className="h-3 w-3" />
                        Common Ground Found
                      </h4>
                      <div className="space-y-1.5">
                        {commonGround.map((cg, index) => {
                          const cgType = cg.type as string;
                          return (
                            <div key={index} className="flex items-start gap-2">
                              {(cgType === 'city' || cgType === 'metro') && <MapPin className="h-3 w-3 text-green-400 mt-0.5 flex-shrink-0" />}
                              {(cgType === 'college' || cgType === 'school') && <GraduationCap className="h-3 w-3 text-green-400 mt-0.5 flex-shrink-0" />}
                              {cgType === 'industry' && <Building2 className="h-3 w-3 text-green-400 mt-0.5 flex-shrink-0" />}
                              {(cgType === 'workplace' || cgType === 'employer') && <Briefcase className="h-3 w-3 text-green-400 mt-0.5 flex-shrink-0" />}
                              {cgType === 'role' && <User className="h-3 w-3 text-green-400 mt-0.5 flex-shrink-0" />}
                              <span className="text-xs text-[#F5F5F5]">{(cg as any).details || (cg as any).detail}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Grab a Coffee - Same City Suggestion */}
                  {sameCity && (
                    <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
                      <div className="flex items-center gap-2">
                        <Coffee className="h-4 w-4 text-amber-400" />
                        <div>
                          <p className="text-xs font-medium text-amber-400">Same City!</p>
                          <p className="text-[10px] text-amber-300/70">Consider suggesting a coffee meeting in your email</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Generation Error */}
                  {generationError && (
                    <div className="mt-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2">
                      <p className="text-xs text-red-400">{generationError}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-5">
              <h3 className="text-xs font-medium text-cacao-muted">Draft text (SMS)</h3>
              {selectedLead && leadHasCallablePhone(selectedLead) ? (
                <div className="mt-2 space-y-2">
                  <textarea
                    value={smsDraftBody}
                    onChange={(e) => setSmsDraftBody(e.target.value)}
                    rows={4}
                    className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark px-3 py-2 text-xs text-[#F5F5F5] placeholder-cacao-text-tertiary leading-relaxed focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                    placeholder="Short text to send..."
                    spellCheck
                  />
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => {
                        setSmsDraftBody(defaultSmsBody(selectedLead, userProfile));
                        toast.info('Regenerated', 'SMS draft reset to the default template.');
                      }}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-cacao-dark-tertiary px-3 py-2 text-[10px] font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Generate text
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        await navigator.clipboard.writeText(smsDraftBody);
                        setCopiedSms(true);
                        setTimeout(() => setCopiedSms(false), 2000);
                      }}
                      className={classNames(
                        'flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-[10px] font-medium transition-colors',
                        copiedSms
                          ? 'border-green-500/50 text-green-400 bg-green-500/10'
                          : 'border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold hover:text-white'
                      )}
                    >
                      {copiedSms ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      {copiedSms ? 'Copied' : 'Copy'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const url = buildSmsDraftUrl(getLeadPhoneForUi(selectedLead)!, smsDraftBody);
                        if (!url) {
                          toast.error('Invalid phone', 'Need at least 10 digits.');
                          return;
                        }
                        window.location.assign(url);
                      }}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-green-600/90 px-3 py-2 text-[10px] font-semibold text-white hover:bg-green-600 transition-colors"
                    >
                      <MessageSquare className="h-3 w-3" />
                      Open in Messages
                    </button>
                  </div>
                  <p className="text-[10px] text-cacao-text-tertiary">
                    Uses the sms: link so macOS or iOS can open Messages with this thread. Body prefill works on most Apple devices; behavior can vary by OS version.
                  </p>
                </div>
              ) : (
                <p className="mt-2 text-xs text-cacao-text-tertiary">
                  Enrich profile (Apollo) or add a phone number here to draft a text and open it in Messages.
                </p>
              )}
            </div>

            {canUseGmail &&
              hasDraft &&
              selectedLead &&
              statusesWithGmailCompose.includes(selectedLead.status) && (
              <div className="mt-5 space-y-2">
                <div
                  className={
                    showMarkSentNextToGmail
                      ? 'flex flex-col gap-2 sm:flex-row sm:items-stretch'
                      : ''
                  }
                >
                  <a
                    href={`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(selectedLead.email)}&su=${encodeURIComponent(draftSubject || 'Quick intro')}&body=${encodeURIComponent(draftBody || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      if (gmailAccessToken && selectedLead.status === 'pending') {
                        startGmailSentWatch(selectedLead.id);
                      }
                    }}
                    className={
                      showMarkSentNextToGmail
                        ? 'flex flex-1 items-center justify-center gap-2 rounded-md bg-cacao-gold px-3 py-2 text-xs font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors'
                        : 'flex w-full items-center justify-center gap-2 rounded-md bg-cacao-gold px-3 py-2 text-xs font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors'
                    }
                  >
                    <Mail className="h-3.5 w-3.5" />
                    Open in Gmail
                  </a>
                  {showMarkSentNextToGmail && (
                    <button
                      type="button"
                      onClick={() => handleAction(selectedLead.id, 'approve')}
                      disabled={!selectedLead.email || selectedLead.email.includes('@apollo.local')}
                      className="flex flex-1 items-center justify-center rounded-md border border-cacao-gold/50 px-3 py-2 text-xs font-medium text-cacao-gold hover:bg-cacao-gold/10 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Mark as sent
                    </button>
                  )}
                </div>
                <p className="text-center text-[10px] text-cacao-text-tertiary">
                  {gmailAccessToken ? (
                    <>
                      While this lead is open, we check your Gmail Sent folder periodically (and when you return to
                      this tab) and mark the lead as sent when we see a message to this address. You can still use{' '}
                      <span className="font-medium text-cacao-text-secondary">Mark as sent</span> anytime. Sign in with
                      Google is required for auto-detect.
                    </>
                  ) : (
                    <>
                      Sign in with Google so we can detect sends from Gmail, or tap{' '}
                      <span className="font-medium text-cacao-text-secondary">Mark as sent</span> after you send.
                    </>
                  )}{' '}
                  Generated drafts append your signature and send-as block (API compose doesn&apos;t auto-insert them);
                  re-sign in with Google if that block is missing.
                </p>
              </div>
            )}

            {selectedLead.status === 'pending' && (
              <div className="mt-5 flex flex-col gap-2">
                {!canUseGmail && (
                  <div className="rounded-md border border-dashed border-cacao-dark-tertiary bg-cacao-dark px-3 py-2 text-center text-xs text-cacao-muted">
                    Get email first to send message
                  </div>
                )}
                <div className="flex gap-2">
                  {!showMarkSentNextToGmail && (
                    <button
                      onClick={() => handleAction(selectedLead.id, 'approve')}
                      disabled={!selectedLead.email || selectedLead.email.includes('@apollo.local')}
                      className="flex-1 rounded-md border border-cacao-gold/50 px-3 py-2 text-xs font-medium text-cacao-gold hover:bg-cacao-gold/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Mark as Sent
                    </button>
                  )}
                  <button
                    onClick={() => handleAction(selectedLead.id, 'skip')}
                    className={
                      showMarkSentNextToGmail
                        ? 'w-full rounded-md border border-cacao-dark-tertiary px-3 py-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors'
                        : 'flex-1 rounded-md border border-cacao-dark-tertiary px-3 py-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors'
                    }
                  >
                    Skip
                  </button>
                </div>
                <div className="mt-2">
                  <button
                    onClick={() => handleArchiveLead(selectedLead.id)}
                    className="w-full flex items-center justify-center gap-2 rounded-md border border-cacao-dark-tertiary px-3 py-2 text-xs font-medium text-cacao-muted hover:border-red-500/30 hover:text-red-400 transition-colors"
                  >
                    <Archive className="h-3.5 w-3.5" />
                    Archive Lead
                  </button>
                </div>
              </div>
            )}

          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center p-6 text-center">
            {/* Animated illustration */}
            <div className="relative mb-6">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-cacao-dark-tertiary to-cacao-dark flex items-center justify-center animate-float">
                <User className="h-10 w-10 text-cacao-muted/50" />
              </div>
              {/* Animated ring */}
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-cacao-dark-tertiary animate-spin" style={{ animationDuration: '20s' }} />
            </div>
            <p className="text-sm font-medium text-cacao-muted">Select a Lead</p>
            <p className="mt-1 text-xs text-cacao-text-tertiary">
              Click on a lead to see their full profile and draft your message
            </p>
            {/* Quick tips */}
            <div className="mt-6 space-y-2 text-left w-full max-w-[200px]">
              <div className="flex items-center gap-2 text-[10px] text-cacao-text-tertiary">
                <div className="h-1 w-1 rounded-full bg-amber-400" />
                <span>📍 Local = same city as you</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-cacao-text-tertiary">
                <div className="h-1 w-1 rounded-full bg-green-400" />
                <span>🤝 Connected = shared background</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-cacao-text-tertiary">
                <div className="h-1 w-1 rounded-full bg-blue-400" />
                <span>📧 New = fresh contacts</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
